(function () {
  'use strict';

  if (window.KinsoraCartRedesign) return;

  // Cart choices are calculated from rendered catalog data. Only an explicit
  // shopper action can start a cart request.

  var state = {
    activeSheet: null,
    trigger: null,
    mode: 'add',
    pairId: '',
    oldFreeKey: '',
    oldFreeVariantId: 0,
    paidPrice: 0,
    completedPairCount: 0,
    busy: false,
    mutationSerial: 0,
    latestCommittedSerial: 0,
    sheetSerial: 0
  };

  var sheetIsolation = null;

  function syncVisualViewportInset() {
    var viewport = window.visualViewport;
    var bottomInset = 0;

    if (viewport) {
      bottomInset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
    }

    document.documentElement.style.setProperty('--ks-visual-viewport-bottom', Math.round(bottomInset) + 'px');
  }

  function drawer() {
    return document.querySelector('#cartDrawer[data-kinsora-cart-redesign="true"]');
  }

  // Presentation only. This timer never writes to the cart or reserves inventory.
  var cartCountdownStorageKey = 'kinsora.cartCountdown.v1';
  var cartCountdownDuration = 10 * 60 * 1000;
  var cartCountdownDeadline = null;
  var cartCountdownTimeout = null;

  function readCartCountdownDeadline() {
    try {
      var value = Number(window.localStorage.getItem(cartCountdownStorageKey));
      return Number.isFinite(value) && value > 0 ? value : null;
    } catch (error) { return null; }
  }

  function refreshCartCountdown() {
    if (cartCountdownTimeout !== null) window.clearTimeout(cartCountdownTimeout);
    cartCountdownTimeout = null;
    var root = drawer();
    if (!root) return;
    var timers = root.querySelectorAll('[data-ks-cart-timer]');
    var itemCount = Number(root.dataset.cartItemCount);
    if (root.dataset.cartItemCount == null || root.dataset.cartItemCount === '' || !Number.isFinite(itemCount) || itemCount < 0) return;
    if (itemCount === 0) {
      cartCountdownDeadline = null;
      try { window.localStorage.removeItem(cartCountdownStorageKey); } catch (error) {}
      timers.forEach(function (timer) { timer.hidden = true; });
      return;
    }
    if (!timers.length) return;
    if (cartCountdownDeadline === null) {
      cartCountdownDeadline = readCartCountdownDeadline();
      if (cartCountdownDeadline === null) {
        cartCountdownDeadline = Date.now() + cartCountdownDuration;
        try { window.localStorage.setItem(cartCountdownStorageKey, String(cartCountdownDeadline)); } catch (error) {}
      }
    }
    var milliseconds = Math.max(0, cartCountdownDeadline - Date.now());
    var seconds = Math.min(600, Math.ceil(milliseconds / 1000));
    var label = String(Math.floor(seconds / 60)).padStart(2, '0') + ':' + String(seconds % 60).padStart(2, '0');
    timers.forEach(function (timer) {
      var value = timer.querySelector('[data-ks-cart-timer-value]');
      if (value && value.textContent !== label) value.textContent = label;
      // Hide expired timers while retaining the deadline across cart refreshes.
      timer.hidden = seconds === 0;
    });
    if (seconds > 0 && !document.hidden && root.classList.contains('open')) {
      cartCountdownTimeout = window.setTimeout(refreshCartCountdown, Math.min(1000, milliseconds));
    }
  }

  function selectorEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(value));
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function resetPickerState() {
    state.mode = 'add';
    state.pairId = '';
    state.oldFreeKey = '';
    state.oldFreeVariantId = 0;
    state.paidPrice = 0;
    state.completedPairCount = 0;
  }

  function money(cents) {
    if (window.Kinsora && typeof window.Kinsora.formatMoney === 'function') {
      return window.Kinsora.formatMoney(Number(cents) || 0);
    }
    return '$' + ((Number(cents) || 0) / 100).toFixed(2);
  }

  function announce(message) {
    var node = document.querySelector('[data-ks-cart-announcer]');
    if (!node) return;
    node.textContent = '';
    window.setTimeout(function () { node.textContent = message || ''; }, 30);
  }

  function sheetStatus(sheet, message) {
    if (!sheet) return;
    var node = sheet.querySelector('[data-ks-sheet-status]');
    if (sheet.dataset.ksSheet === 'picker') putBogoText(node, message);
    else putText(node, message || '');
  }

  function focusableWithin(root) {
    if (!root) return [];
    return Array.prototype.filter.call(
      root.querySelectorAll('button:not([disabled]), a[href], select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (node) { return !node.hidden && node.offsetParent !== null; }
    );
  }

  function focusWithoutScroll(node) {
    if (!node || typeof node.focus !== 'function') return false;
    try { node.focus({ preventScroll: true }); }
    catch (error) { node.focus(); }
    return document.activeElement === node || node.contains(document.activeElement);
  }

  function accessibilityState(node) {
    return {
      node: node,
      hadInert: node.hasAttribute('inert'),
      ariaHidden: node.getAttribute('aria-hidden')
    };
  }

  function restoreAccessibilityState(record) {
    if (!record || !record.node) return;
    if (record.hadInert) record.node.setAttribute('inert', '');
    else record.node.removeAttribute('inert');
    if (record.ariaHidden === null) record.node.removeAttribute('aria-hidden');
    else record.node.setAttribute('aria-hidden', record.ariaHidden);
  }

  function isolateCartForSheet(root, sheet, focusTarget) {
    if (!root || !sheet) return;
    var siblings = [];
    sheetIsolation = {
      sheet: sheet,
      sheetState: accessibilityState(sheet),
      siblings: siblings
    };
    sheet.removeAttribute('inert');
    sheet.removeAttribute('aria-hidden');
    if (!focusWithoutScroll(focusTarget) && root.contains(document.activeElement) && !sheet.contains(document.activeElement)) {
      if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    }
    Array.prototype.forEach.call(root.children, function (child) {
      if (child === sheet || child.tagName === 'SCRIPT') return;
      siblings.push(accessibilityState(child));
      child.setAttribute('inert', '');
      child.setAttribute('aria-hidden', 'true');
    });
  }

  function releaseSheetIsolation() {
    if (!sheetIsolation) return null;
    var isolation = sheetIsolation;
    sheetIsolation = null;
    isolation.siblings.forEach(restoreAccessibilityState);
    return isolation.sheetState;
  }

  function resetActiveSheet(options) {
    options = options || {};
    var activeSheet = state.activeSheet || (sheetIsolation && sheetIsolation.sheet);
    if (!activeSheet && !sheetIsolation) return;
    var root = drawer();
    var trigger = state.trigger;
    var wasPicker = !!(activeSheet && activeSheet.dataset.ksSheet === 'picker');
    var focusWasInside = !!(activeSheet && activeSheet.contains(document.activeElement));
    var activeSheetState = releaseSheetIsolation();

    state.sheetSerial += 1;
    state.activeSheet = null;
    state.trigger = null;
    document.documentElement.classList.remove('ks-cart-sheet-open');

    if (focusWasInside) {
      var focusTarget = options.returnFocus && trigger && trigger.isConnected
        ? trigger
        : null;
      if (!focusTarget && options.focusFallback && root && root.isConnected) {
        focusTarget = root.querySelector('#ksCartTitle') || root.querySelector('.cart-close-btn');
      }
      if (!focusWithoutScroll(focusTarget) && document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }
    }

    if (activeSheet && activeSheet.isConnected) activeSheet.hidden = true;
    restoreAccessibilityState(activeSheetState);
    if (wasPicker) resetPickerState();
  }

  function reconcileActiveSheet(focusFallback) {
    if (!state.activeSheet && !sheetIsolation) return;
    var root = drawer();
    var activeSheet = state.activeSheet || (sheetIsolation && sheetIsolation.sheet);
    if (!root || !activeSheet || !activeSheet.isConnected || !root.contains(activeSheet) || activeSheet.hidden) {
      resetActiveSheet({ returnFocus: false, focusFallback: !!focusFallback });
    }
  }

  function openSheet(name, trigger) {
    if (name === 'picker') return; // The cart chooser is inline, never a second sheet.
    var root = drawer();
    var sheet = root && root.querySelector('[data-ks-sheet="' + name + '"]');
    if (!sheet) return;
    syncVisualViewportInset();
    closeSheet(false);
    var serial = ++state.sheetSerial;
    state.activeSheet = sheet;
    state.trigger = trigger || document.activeElement;
    sheet.hidden = false;
    var panel = sheet.querySelector('.ks-sheet__panel');
    isolateCartForSheet(root, sheet, panel);
    document.documentElement.classList.add('ks-cart-sheet-open');
    if (panel) panel.scrollTop = 0;
    window.requestAnimationFrame(function () {
      if (serial !== state.sheetSerial || state.activeSheet !== sheet || !sheet.isConnected || sheet.hidden) return;
      if (panel) panel.scrollTop = 0;
      var focusable = focusableWithin(panel);
      focusWithoutScroll(focusable[0] || panel);
    });
  }

  function closeSheet(returnFocus) {
    resetActiveSheet({ returnFocus: returnFocus !== false, focusFallback: true });
  }

  function fetchJson(path, options) {
    var requestPath = path;
    if (/^\/?cart(?:\/|\.)/.test(String(path || ''))) {
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
      requestPath = root + String(path).replace(/^\//, '');
    }
    var request = options || {};
    request.credentials = 'same-origin';
    request.headers = request.headers || {};
    request.headers.Accept = 'application/json';
    // Bound both transport and body decoding, even if abort is ignored.
    // A timed-out write may still commit; never replay it automatically.
    var controller = new AbortController();
    var timer;
    var deadline = new Promise(function (resolve, reject) {
      timer = window.setTimeout(function () {
        var error = new Error('The cart request timed out. Please refresh and review your cart before trying again.');
        error.kinsoraCartRequestTimeout = true;
        var method = String(request.method || 'GET').toUpperCase();
        if (method !== 'GET' && method !== 'HEAD') error.kinsoraUnsafeCartState = true;
        reject(error);
        controller.abort();
      }, 15000);
    });
    var responseBody = Promise.resolve().then(function () {
      return fetch(requestPath, Object.assign({}, request, { signal: controller.signal }));
    }).then(function (response) {
      if (!response.ok) {
        return response.text().then(function (body) {
          throw new Error(requestPath + ' ' + response.status + ' ' + String(body || '').slice(0, 180));
        });
      }
      return response.json();
    });
    return Promise.race([responseBody, deadline]).finally(function () {
      window.clearTimeout(timer);
    });
  }

  function setBusy(control, busy) {
    if (!control) return;
    if (busy) {
      control.dataset.ksPreviousHtml = control.innerHTML;
      control.disabled = true;
      control.setAttribute('aria-busy', 'true');
      control.innerHTML = '<span class="ks-cart-busy-spinner" aria-hidden="true"></span>';
      var busyCopy = document.createElement('span');
      busyCopy.className = control.dataset.ksBusyLabel ? 'ks-cart-busy-label' : 'visually-hidden';
      busyCopy.textContent = control.dataset.ksBusyLabel || 'Updating cart';
      control.appendChild(busyCopy);
    } else {
      control.disabled = false;
      control.removeAttribute('aria-busy');
      if (control.dataset.ksPreviousHtml) {
        control.innerHTML = control.dataset.ksPreviousHtml;
        delete control.dataset.ksPreviousHtml;
      }
    }
  }

  function delay(milliseconds) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, milliseconds);
    });
  }

  function pendingBogoCount() {
    var root = drawer();
    return Math.max(0, Number(root && root.dataset.bogoPendingCount || 0));
  }

  function isBogoDiscountTitle(title) {
    var normalized = String(title || '').toLowerCase();
    return normalized.indexOf('buy 1, get') !== -1 ||
      normalized.indexOf('buy 1, get') !== -1 ||
      normalized.indexOf('buy 1, get') !== -1 ||
      normalized.indexOf('bogo') !== -1;
  }

  function collectBogoPricing(selected) {
    var root = drawer();
    var pricing = { currency: root && root.dataset.presentmentCurrency || '', prices: {} };
    function record(variantId, originalPrice, eligiblePrice) {
      if (!Number.isInteger(variantId) || variantId <= 0 || !Number.isInteger(originalPrice) || originalPrice <= 0
        || !Number.isInteger(eligiblePrice) || eligiblePrice <= 0 || eligiblePrice > originalPrice) return;
      var key = variantId + ':' + originalPrice;
      if (Object.prototype.hasOwnProperty.call(pricing.prices, key) && pricing.prices[key] !== eligiblePrice) {
        pricing.prices[key] = 0;
      } else {
        pricing.prices[key] = eligiblePrice;
      }
    }
    if (root) {
      root.querySelectorAll('[data-ks-cart-line][data-bogo-eligible="true"]').forEach(function (line) {
        var quantity = Number(line.dataset.lineQuantity);
        if (!Number.isInteger(quantity) || quantity < 1) return;
        record(Number(line.dataset.variantId), Number(line.dataset.lineOriginal) / quantity, Number(line.dataset.eligiblePrice));
      });
    }
    if (selected) record(Number(selected.variantId), Number(selected.originalPrice || selected.price), Number(selected.price));
    return pricing;
  }

  function bogoUnitPrice(item, pricing) {
    var originalPrice = Number(item.original_price);
    if (!pricing) return originalPrice;
    var key = Number(item.variant_id || item.id) + ':' + originalPrice;
    var price = Number(pricing.prices && pricing.prices[key]);
    return Number.isInteger(price) && price > 0 && price <= originalPrice ? price : 0;
  }

  function actualBogoFreeCount(cart, pricing) {
    if (!cart || !Array.isArray(cart.items) || (pricing && pricing.currency && cart.currency && pricing.currency !== cart.currency)) return 0;
    return cart.items.reduce(function (count, item) {
      var isGift = item.properties && String(item.properties._kinsora_free_gift || '').toLowerCase() === 'true';
      var unitPrice = bogoUnitPrice(item, pricing);
      var quantity = Number(item.quantity);
      if (isGift || !Number.isInteger(unitPrice) || unitPrice < 1 || !Number.isInteger(quantity) || quantity < 1) return count;
      var allocations = Array.isArray(item.line_level_discount_allocations)
        ? item.line_level_discount_allocations
        : [];
      var pairMarker = item.properties && String(item.properties._kinsora_bogo_pair || '');
      var markedFreeLine = !!pairMarker && Number(item.final_line_price) === 0;
      var bogoDiscount = allocations.reduce(function (total, allocation) {
        var application = allocation && allocation.discount_application || {};
        /* The pair property is owned by this theme and survives app-side title
           changes. Only use it as a fallback when Shopify made the marked line
           fully free, so unrelated automatic discounts cannot be misclassified. */
        return isBogoDiscountTitle(application.title) || markedFreeLine
          ? total + Number(allocation.amount || 0)
          : total;
      }, 0);
      // Set markdowns can share the BOGO title. Count only the value made free
      // after the set price, matching the server-rendered cart and savings rows.
      var availableBogoDiscount = Math.max(0, unitPrice * quantity - Number(item.final_line_price));
      var freeUnits = Math.floor(Math.min(bogoDiscount, availableBogoDiscount) / unitPrice);
      return count + Math.max(0, Math.min(Number(item.quantity) || 0, freeUnits));
    }, 0);
  }

  function cartMerchandiseSnapshot(cart, excludedLine, pricing) {
    if (!cart || !Array.isArray(cart.items)) return null;
    var snapshot = {};
    function propertiesOf(item) {
      var properties = item.properties || {};
      return Object.keys(properties).sort().map(function (name) { return [name, properties[name]]; });
    }
    cart.items.forEach(function (item) {
      if (item === excludedLine || String(item.properties && item.properties._kinsora_free_gift || '').toLowerCase() === 'true') return;
      var components = (Array.isArray(item.item_components) ? item.item_components : []).map(function (component) {
        return [Number(component.variant_id || component.id), Number(component.quantity), propertiesOf(component)];
      }).sort(function (a, b) { return JSON.stringify(a).localeCompare(JSON.stringify(b)); });
      var identity = JSON.stringify([
        Number(item.variant_id || item.id),
        propertiesOf(item),
        item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id || null,
        Number(item.original_price),
        components
      ]);
      if (!snapshot[identity]) snapshot[identity] = { quantity: 0, freeUnits: 0, price: bogoUnitPrice(item, pricing) };
      snapshot[identity].quantity += Number(item.quantity);
      snapshot[identity].freeUnits += actualBogoFreeCount({ items: [item] }, pricing);
    });
    return snapshot;
  }

  function verifiedNoCostBogoTie(baselineCart, cart, addedLine, selected, pricing) {
    if (!baselineCart || !cart || !Array.isArray(baselineCart.items) || !Array.isArray(cart.items) || !addedLine || !selected) return false;
    var cartUnit = String(addedLine.properties && addedLine.properties._kinsora_cart_unit || '');
    var selectedPrice = Number(selected.price);
    var selectedOriginalPrice = Number(selected.originalPrice || selected.price);
    if (!cartUnit || cart.items.indexOf(addedLine) === -1 || Number(addedLine.variant_id || addedLine.id) !== Number(selected.variantId)
      || Number(addedLine.quantity) !== 1 || !Number.isInteger(selectedPrice) || selectedPrice <= 0
      || !Number.isInteger(selectedOriginalPrice) || selectedOriginalPrice < selectedPrice
      || Number(addedLine.original_price) !== selectedOriginalPrice || Number(addedLine.final_line_price) !== selectedPrice
      || bogoUnitPrice(addedLine, pricing) !== selectedPrice) return false;
    if (baselineCart.items.some(function (item) { return String(item.properties && item.properties._kinsora_cart_unit || '') === cartUnit; })
      || cart.items.filter(function (item) { return String(item.properties && item.properties._kinsora_cart_unit || '') === cartUnit; }).length !== 1) return false;
    if (!baselineCart.currency || baselineCart.currency !== cart.currency || (pricing && pricing.currency && pricing.currency !== cart.currency) || baselineCart.total_price == null || cart.total_price == null
      || !Number.isInteger(Number(baselineCart.total_price)) || Number(baselineCart.total_price) < 0
      || Number(cart.total_price) !== Number(baselineCart.total_price)
      || actualBogoFreeCount(cart, pricing) !== actualBogoFreeCount(baselineCart, pricing) + 1) return false;

    // Shopify can assign the free unit to an existing product in a price tie.
    // Verify the actual result without relying on discount-sensitive line keys.
    var before = cartMerchandiseSnapshot(baselineCart, null, pricing);
    var after = cartMerchandiseSnapshot(cart, addedLine, pricing);
    var identities = Object.keys(before).sort();
    if (JSON.stringify(identities) !== JSON.stringify(Object.keys(after).sort())) return false;
    if (!identities.every(function (identity) {
      return Number.isInteger(before[identity].quantity) && before[identity].quantity > 0
        && before[identity].quantity === after[identity].quantity;
    })) return false;
    return identities.some(function (identity) {
      return before[identity].price === selectedPrice && after[identity].freeUnits > before[identity].freeUnits;
    });
  }

  function showPendingBogoFailure(root) {
    var currentRoot = root && root.isConnected ? root : drawer();
    if (!currentRoot || Number(currentRoot.dataset.bogoPendingCount || 0) < 1) return;
    currentRoot.querySelectorAll('[data-ks-bogo-pending-status]:not(.is-error)').forEach(function (status) {
      status.classList.add('is-error');
      status.setAttribute('role', 'alert');
      status.innerHTML = '';

      var copy = document.createElement('span');
      copy.textContent = 'Your Buy 1, Get 1 savings could not be confirmed. Retry, or remove an item below before checkout.';
      status.appendChild(copy);

      var retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'ks-cart-promotion-status__recovery';
      retry.dataset.ksRetryBogo = '';
      retry.textContent = 'Retry savings';
      status.appendChild(retry);
    });
  }

  function settlePendingBogoDiscounts(serial, attempt) {
    if (serial < state.latestCommittedSerial || pendingBogoCount() < 1) {
      return Promise.resolve();
    }
    if (attempt >= 3) {
      showPendingBogoFailure(drawer());
      return Promise.resolve();
    }
    if (!window.Kinsora || typeof window.Kinsora.refreshCartDrawer !== 'function') {
      showPendingBogoFailure(drawer());
      return Promise.resolve();
    }
    return delay(250 + (attempt * 250))
      .then(function () {
        if (serial < state.latestCommittedSerial) return null;
        return window.Kinsora.refreshCartDrawer();
      })
      .then(function () {
        return settlePendingBogoDiscounts(serial, attempt + 1);
      });
  }

  function reconcilePendingBogoStatus(root) {
    if (!root) return;
    var status = root.querySelector('[data-ks-bogo-pending-status]');
    if (Number(root.dataset.bogoPendingCount || 0) < 1) {
      if (status) status.remove();
      return;
    }
    if (status) return;
    var items = root.querySelector('#cartItems.ks-cart-items');
    if (!items) return;

    status = document.createElement('div');
    status.className = 'ks-cart-promotion-status';
    status.dataset.ksBogoPendingStatus = '';
    status.setAttribute('role', 'status');
    status.innerHTML = '<span class="ks-cart-busy-spinner" aria-hidden="true"></span><span>Confirming your Buy 1, Get 1 savings…</span>';
    items.insertBefore(status, items.firstChild);
    window.setTimeout(function () {
      // A replaced drawer has its own verification timer. Do not let an old
      // render turn the current notice into an error before its checks finish.
      if (root.isConnected && status.isConnected) showPendingBogoFailure(root);
    }, 3500);
  }

  function retryPendingBogoSavings(control) {
    if (state.busy) return;
    if (!window.Kinsora || typeof window.Kinsora.ensureCanonicalGiftState !== 'function') {
      window.location.reload();
      return;
    }
    state.busy = true;
    setBusy(control, true);
    return Promise.resolve().then(function () {
      return window.Kinsora.ensureCanonicalGiftState('retry-savings');
    }).then(function () {
      announce('Your cart savings are confirmed.');
    }).catch(function () {
      showPendingBogoFailure(drawer());
      announce('Savings could not be confirmed. Please try again.');
    }).then(function () {
      setBusy(control, false);
      state.busy = false;
      var currentRoot = drawer();
      var retry = currentRoot && currentRoot.querySelector('[data-ks-retry-bogo]');
      focusWithoutScroll(retry || document.getElementById('ksCartTitle'));
    });
  }

  function reconcileAndRender(cart, serial, message, alreadySynchronized) {
    var hadActiveSheet = !!state.activeSheet;
    var reconciliation = Promise.resolve({ cart: cart, changed: false });
    if (!alreadySynchronized && window.Kinsora && typeof window.Kinsora.reconcileFreeGift === 'function') {
      reconciliation = Promise.resolve(window.Kinsora.reconcileFreeGift(cart, { source: 'redesigned-cart' }));
    }
    return reconciliation.then(function (result) {
      var authoritativeCart = result && result.cart ? result.cart : cart;
      if (serial < state.latestCommittedSerial) return authoritativeCart;
      state.latestCommittedSerial = serial;
      var finishRender = function () {
        return settlePendingBogoDiscounts(serial, 0).then(function () {
          resetActiveSheet({ returnFocus: false, focusFallback: false });
          resetPickerState();
          if (hadActiveSheet) {
            var refreshedTitle = document.getElementById('ksCartTitle');
            focusWithoutScroll(refreshedTitle);
          }
          announce(message);
          return authoritativeCart;
        });
      };
      // The shared paid-mutation gate has already reconciled, rendered, and
      // canonically verified this cart. Do not perform a second refresh.
      if (alreadySynchronized) return finishRender();
      if (window.Kinsora && typeof window.Kinsora.refreshCartDrawer === 'function') {
        return Promise.resolve(window.Kinsora.refreshCartDrawer()).then(function () {
          return finishRender();
        });
      }
      window.location.reload();
      return authoritativeCart;
    });
  }

  function runMutation(control, operation, successMessage, options) {
    options = options || {};
    if (inlineCartUpdating()) return Promise.resolve(null);
    captureInlineView();
    state.busy = true;
    var serial = ++state.mutationSerial;
    setBusy(control, true);
    var readyRunner = !options.operationOwnsSynchronization
      && window.Kinsora && typeof window.Kinsora.runWhenSynchronizedPaidCartMutationReady === 'function'
      ? window.Kinsora.runWhenSynchronizedPaidCartMutationReady
      : null;
    var usesSynchronization = options.operationOwnsSynchronization === true || !!readyRunner;
    var request = options.operationOwnsSynchronization === true
      ? Promise.resolve().then(operation)
      : readyRunner
        ? readyRunner(operation)
        : Promise.reject(new Error('The secure cart runtime is unavailable.'));
    return request
      .then(function (cart) {
        return reconcileAndRender(
          cart,
          serial,
          successMessage,
          usesSynchronization
        );
      })
      .catch(function (error) {
        console.error('[Kinsora cart redesign]', error);
        var failureMessage = error && error.kinsoraCartRequestTimeout
          ? 'The cart request timed out. Please refresh and review your cart before trying again.'
          : 'Something went wrong. Your cart is still available. Please try again.';
        sheetStatus(state.activeSheet, failureMessage);
        announce(failureMessage);
        setBusy(control, false);
        throw error;
      })
      .then(function (cart) {
        // The full drawer refresh normally replaces the initiating control.
        // If Shopify accepted the cart mutation but the section refresh could
        // not be rendered, restore the still-connected control instead of
        // leaving a permanent spinner/disabled state in the current drawer.
        setBusy(control, false);
        state.busy = false;
        if (!options.inlineChoice) startInline();
        return cart;
      }, function () {
        setBusy(control, false);
        state.busy = false;
        if (!options.inlineChoice) startInline();
        return null;
      });
  }

  var inline = {node: null, serial: 0, cart: null, session: null, offers: {}, cards: [], displayed: [], cursor: 0,
    shown: 4, loading: false, removeKey: '', scrollTop: 0, notice: '', noticeError: false, noticeFingerprint: '', capacity: 1,
    exposure: '', checkoutEvent: '', pendingControl: null, pendingLocks: [], catalog: {}, catalogNode: null, products: [], eligible: [], productsNode: null, templateNode: null, productsSection: null, productsLoaded: false, view: null, frozenChooser: null, stagedChooser: null};

  function inlineCartUpdating() {
    return state.busy || document.documentElement.classList.contains('kinsora-free-gift-reconciling')
      || !!(window.Kinsora && typeof window.Kinsora.isCartQuantityPending === 'function' && window.Kinsora.isCartQuantityPending());
  }
  function freezeInlineAction(section) {
    section.dataset.ksPendingAction = 'true';
    section.querySelectorAll('button, select, a').forEach(function (control) {
      if (!inline.pendingLocks.some(function (record) { return record.control === control; })) {
        inline.pendingLocks.push({control: control, disabled: control === inline.pendingControl ? false : control.disabled,
          inert: control.hasAttribute('inert'), tabIndex: control.getAttribute('tabindex'), ariaDisabled: control.getAttribute('aria-disabled')});
      }
      if (control.tagName === 'A') {
        control.setAttribute('inert', '');
        control.setAttribute('tabindex', '-1');
        control.setAttribute('aria-disabled', 'true');
      } else control.disabled = true;
    });
  }
  function releaseInlineAction() {
    inline.pendingLocks.splice(0).forEach(function (record) {
      var control = record.control;
      if (!control.isConnected) return;
      if (control.tagName !== 'A') { control.disabled = record.disabled; return; }
      if (!record.inert) control.removeAttribute('inert');
      if (record.tabIndex === null) control.removeAttribute('tabindex');
      else control.setAttribute('tabindex', record.tabIndex);
      if (record.ariaDisabled === null) control.removeAttribute('aria-disabled');
      else control.setAttribute('aria-disabled', record.ariaDisabled);
    });
  }
  function captureInlineView() {
    var root = drawer(), body = root && root.querySelector('#cartBody');
    if (!root || !body) return;
    if (!inline.view) {
      var active = document.activeElement;
      var inside = !!active && root.contains(active);
      var line = inside && active.closest && active.closest('[data-ks-cart-line]');
      var action = inside && active.getAttribute && active.getAttribute('data-action');
      var selector = action === 'cart-quantity-minus' || action === 'cart-quantity-plus'
        ? '[data-action="' + action + '"]'
        : inside && active.hasAttribute && active.hasAttribute('data-ks-remove-line') ? '[data-ks-remove-line]' : '';
      inline.view = {scrollTop: body.scrollTop, restoreFocus: inside,
        lineKey: line && line.dataset.cartItemKey || '', selector: selector};
    } else inline.view.scrollTop = body.scrollTop;
    inline.scrollTop = inline.view.scrollTop;
  }
  function restoreInlineView() {
    if (!inline.view || inlineCartUpdating()) return;
    var view = inline.view, root = drawer();
    inline.view = null;
    if (!root) return;
    var body = root.querySelector('#cartBody');
    if (body) body.scrollTop = view.scrollTop;
    if (!view.restoreFocus || !root.classList.contains('open') || root.hasAttribute('inert')) return;
    var target = null;
    if (view.lineKey && view.selector) {
      var lines = Array.prototype.filter.call(root.querySelectorAll('[data-ks-cart-line]'), function (line) {
        return line.dataset.cartItemKey === view.lineKey;
      });
      if (lines.length === 1) {
        var controls = lines[0].querySelectorAll(view.selector);
        if (controls.length === 1 && !controls[0].disabled && !controls[0].hidden) target = controls[0];
      }
    }
    focusWithoutScroll(target || root.querySelector('#ksCartTitle') || root.querySelector('.cart-close-btn'));
  }
  function settleInlineMarkup() {
    if (inlineCartUpdating()) return;
    var root = drawer(), frozen = inline.frozenChooser, staged = inline.stagedChooser;
    if (root && frozen && staged && root.contains(frozen) && frozen.parentNode) {
      frozen.parentNode.replaceChild(staged, frozen);
    }
    releaseInlineAction();
    inline.frozenChooser = null; inline.stagedChooser = null;
  }
  function pauseInlineChoices() {
    captureInlineView();
    ++inline.serial;
    inline.node = null; inline.session = null; inline.offers = {}; inline.displayed = []; inline.eligible = [];
    inline.loading = false; inline.filling = false;
    var section = drawer() && drawer().querySelector('[data-ks-inline-chooser]');
    // Keep the existing presentation in place while its action settles, but
    // invalidate all eligibility and lock every choice before another click.
    if (section && !section.hidden) {
      inline.frozenChooser = section;
      freezeInlineAction(section);
    }
  }
  var inlineSharedBusy = document.documentElement.classList.contains('kinsora-free-gift-reconciling');
  new MutationObserver(function () {
    var busy = document.documentElement.classList.contains('kinsora-free-gift-reconciling');
    if (busy === inlineSharedBusy) return;
    inlineSharedBusy = busy;
    if (busy) pauseInlineChoices();
    else window.requestAnimationFrame(function () { startInline(); });
  }).observe(document.documentElement, {attributes: true, attributeFilter: ['class']});

  var inlineCopyNode = null, inlineCopy = {};
  function inlineText(key) {
    var root = drawer(), node = root && root.querySelector('[data-ks-inline-copy-data]');
    if (node !== inlineCopyNode) {
      inlineCopyNode = node;
      try { inlineCopy = JSON.parse(node.textContent) || {}; } catch (error) { inlineCopy = {}; }
    }
    return inlineCopy[key] || '';
  }
  function putText(node, text) { if (node && node.textContent !== text) node.textContent = text; }
  // Apply emphasis only to known BOGO copy, without interpreting text as HTML.
  function putBogoText(node, text) {
    if (!node) return;
    var copy = String(text || '').replace(/\bfree\b/gi, 'FREE');
    var words = copy.match(/\bFREE\b/g) || [];
    if (node.textContent === copy && node.querySelectorAll('.ks-bogo-free').length === words.length) return;
    node.textContent = '';
    copy.split(/(\bFREE\b)/).forEach(function (part) {
      if (part === 'FREE') {
        var emphasis = document.createElement('span');
        emphasis.className = 'ks-bogo-free';
        emphasis.textContent = part;
        node.appendChild(emphasis);
      } else if (part) node.appendChild(document.createTextNode(part));
    });
  }
  function inlineEvent(name, detail) {
    var root = drawer();
    if (!root) return;
    var event = Object.assign({event: 'kinsora_cart_' + name, theme_id: root.dataset.ksThemeId,
      revision: 'cart-inline-local-v1', qa: root.dataset.ksThemeRole !== 'main', automatic_gift: false}, detail || {});
    document.dispatchEvent(new CustomEvent('kinsora:cart-chooser-event', {detail: event}));
    if (event.qa) return;
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.analyticsProcessingAllowed === 'function' && privacy.analyticsProcessingAllowed()) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(event);
    }
  }
  function checkInlineExposure() {
    var root = drawer(), body = root && root.querySelector('#cartBody');
    if (!root || !body || !root.classList.contains('open') || !inline.session) return;
    var exposureKey = inline.session.fingerprint + ':' + inline.removeKey;
    if (inline.exposure === exposureKey) return;
    var section = root.querySelector('[data-ks-inline-chooser]');
    if (!section || section.hidden) return;
    var bounds = body.getBoundingClientRect();
    var top = Math.max(0, bounds.top), bottom = Math.min(window.innerHeight, bounds.bottom);
    var left = Math.max(0, bounds.left), right = Math.min(window.innerWidth, bounds.right);
    if (bottom <= top || right <= left) return;
    var visibleCount = 0;
    inline.cards.forEach(function (card) {
      if (!card.isConnected || card.hidden || card.dataset.ksPickerCollapsed === 'true' || !inlineOffer(card)) return;
      var box = card.getBoundingClientRect();
      var width = Math.max(0, Math.min(right, box.right) - Math.max(left, box.left));
      var height = Math.max(0, Math.min(bottom, box.bottom) - Math.max(top, box.top));
      // At least half of a verified choice must be visible in the cart's
      // actual scroll viewport. Cards below the fold are not exposures.
      if (box.width > 0 && box.height > 0 && width * height >= box.width * box.height * 0.5) visibleCount += 1;
    });
    if (!visibleCount) return;
    inline.exposure = exposureKey;
    inlineEvent('chooser_exposure', {choice_count: visibleCount});
  }
  function scheduleInlineExposure() {
    if (inline.exposureFrame) return;
    inline.exposureFrame = window.requestAnimationFrame(function () {
      inline.exposureFrame = 0;
      checkInlineExposure();
    });
  }
  document.addEventListener('scroll', function (event) {
    if (event.target === document || event.target && event.target.id === 'cartBody') scheduleInlineExposure();
  }, {capture: true, passive: true});
  window.addEventListener('resize', scheduleInlineExposure, {passive: true});
  function inlineCurrentCart() {
    var root = drawer(), node = root && root.querySelector('[data-ks-inline-cart-state]');
    try { return JSON.parse(node.textContent); } catch (error) { return null; }
  }
  function inlineCatalog() {
    var root = drawer(), node = root && root.querySelector('[data-ks-inline-catalog]');
    try { return JSON.parse(node.textContent); } catch (error) { return {}; }
  }
  function inlineSelections(cart) {
    return (cart && cart.items || []).filter(function (item) {
      var properties = item.properties || {};
      if (String(properties._kinsora_free_gift || '').toLowerCase() === 'true') return false;
      return String(properties._kinsora_cart_choice || '') === 'true'
        || String(properties._kinsora_pdp_role || '') === 'choice'
        || Number(item.final_line_price) === 0 && (item.line_level_discount_allocations || []).some(function (allocation) {
          return Number(allocation.amount) > 0 && isBogoDiscountTitle(allocation.discount_application && allocation.discount_application.title);
        });
    });
  }
  function rankInlineProducts(root) {
    var handles = {}, kinds = {}, maximum = 0;
    root.querySelectorAll('[data-ks-cart-line]:not([data-free-gift="true"])').forEach(function (line) {
      handles[line.dataset.productHandle] = true;
      maximum = Math.max(maximum, Number(line.dataset.eligiblePrice || line.dataset.lineOriginal || 0));
    });
    inline.products.forEach(function (product) {
      if (handles[product.dataset.productHandle]) kinds[product.dataset.productCategory] = true;
    });
    function relevance(product) {
      var data = product.dataset, category = data.productCategory;
      var complementary = kinds.pendants && category === 'chains' || kinds.chains && category === 'pendants';
      return Number(!!handles[data.productHandle]) * 8
        + Number(Number(data.optionPrice) > maximum) * 4
        + (complementary ? 0 : kinds.sets && category === 'sets' ? 1 : 2);
    }
    inline.products.sort(function (a, b) { return relevance(a) - relevance(b) || a.catalogIndex - b.catalogIndex; });
  }
  function inlineOffer(card) {
    var product = card && card.__ksInlineProduct;
    return inline.offers[String(product ? product.selectedVariantId : optionSelection(card).variantId)] || null;
  }
  function setInlineHidden(node, hidden) {
    if (!node || node.hasAttribute('hidden') === hidden) return;
    if (hidden) node.setAttribute('hidden', '');
    else node.removeAttribute('hidden');
  }
  function setInlineAttribute(node, name, value) {
    if (node && node.getAttribute(name) !== String(value)) node.setAttribute(name, String(value));
  }
  function loadInlineProducts(node, template, section) {
    var products;
    try { products = JSON.parse(node.textContent); } catch (error) { products = []; }
    // Accept markup already open in a shopper's tab while the theme upgrades.
    if (!node && !template) {
      var root = drawer();
      products = Array.prototype.map.call(root ? root.querySelectorAll('[data-ks-inline-card]') : [], function (card) {
        var control = card.querySelector('[data-ks-option-variant]');
        var choices = Array.prototype.slice.call(card.querySelectorAll('[data-ks-variant-choice]'));
        var variants = choices.map(function (choice) {
          return Object.assign({}, choice.dataset, {available: choice.dataset.ksVariantAvailable !== 'false'});
        });
        if (!variants.length) variants.push({variantId: control && control.value, available: true});
        return {dataset: Object.assign({}, card.dataset), variants: variants, selectedVariantId: control && control.value, card: card};
      });
    }
    inline.products = Array.isArray(products) ? products.filter(function (product) {
      return product && product.dataset && Array.isArray(product.variants) && product.variants.length;
    }) : [];
    inline.products.forEach(function (product, index) {
      product.catalogIndex = index;
      product.selectedVariantId = String(product.selectedVariantId);
      product.eligibleIndex = -1;
      if (product.card) {
        product.card.__ksInlineProduct = product;
        product.card.__ksInlineRefs = inlineCardRefs(product.card);
        product.card.remove();
      }
    });
    inline.cards = [];
    inline.displayed = []; inline.eligible = [];
    inline.productsNode = node; inline.templateNode = template; inline.productsLoaded = true;
    inline.productsSection = section;
  }
  function inlineCardRefs(card) {
    return {
      control: card.querySelector('[data-ks-option-variant]'),
      button: card.querySelector('[data-ks-add-product]'),
      included: card.querySelector('.ks-cart-option__included'),
      image: card.querySelector('img'),
      placeholder: card.querySelector('.ks-cart-option__placeholder'),
      choices: Array.prototype.slice.call(card.querySelectorAll('[data-ks-variant-choice]'))
    };
  }
  function mountInlineCard(product, fragment) {
    if (product.card) {
      if (!product.card.isConnected) {
        inline.cards.push(product.card);
        fragment.appendChild(product.card);
      }
      return product.card;
    }
    var template = inline.templateNode;
    if (!template || !template.content) return null;
    var source = template.content.querySelector('[data-ks-inline-card]');
    if (!source) return null;
    var card = source.cloneNode(true);
    Object.keys(product.dataset).forEach(function (key) { card.dataset[key] = String(product.dataset[key]); });
    card.__ksInlineProduct = product;
    var refs = card.__ksInlineRefs = inlineCardRefs(card);
    var link = card.querySelector('.ks-cart-option__image');
    if (link) link.setAttribute('href', product.url);
    var title = card.querySelector('.ks-cart-option__title');
    if (title) {
      title.setAttribute('href', product.url);
      title.setAttribute('aria-label', product.titleLabel);
      title.textContent = product.dataset.displayName;
    }
    putText(refs.included, product.dataset.includedItems || '');
    setInlineHidden(refs.included, !product.dataset.includedItems);
    var selected = product.variants.find(function (variant) { return String(variant.variantId) === product.selectedVariantId; });
    var image = product.variants.length > 1 && selected && selected.imageSrc
      ? {src: selected.imageSrc, srcset: selected.imageSrcset} : product.image;
    if (refs.image && image && image.src) {
      refs.image.setAttribute('srcset', image.srcset || '');
      refs.image.setAttribute('src', image.src);
      setInlineHidden(refs.image, false);
      setInlineHidden(refs.placeholder, true);
    } else {
      setInlineHidden(refs.image, true);
      setInlineHidden(refs.placeholder, false);
    }
    var fieldset = card.querySelector('.ks-cart-option__variants');
    setInlineHidden(fieldset, product.variants.length < 2);
    putText(fieldset && fieldset.querySelector('legend'), 'Choose ' + product.dataset.productTitle + ' option');
    if (fieldset && product.variants.length > 1) {
      product.variants.forEach(function (variant) {
        var choice = document.createElement('button');
        choice.type = 'button'; choice.hidden = true;
        choice.title = variant.title; choice.textContent = variant.label;
        choice.setAttribute('data-ks-variant-choice', '');
        choice.dataset.ksVariantAvailable = String(variant.available);
        ['variantId', 'price', 'originalPrice', 'fullPrice', 'contents', 'imageSrc', 'imageSrcset'].forEach(function (key) {
          if (variant[key] !== undefined && variant[key] !== null) choice.dataset[key] = String(variant[key]);
        });
        choice.disabled = variant.available === false;
        if (choice.disabled) {
          var soldOut = document.createElement('small'); soldOut.textContent = 'Sold out'; choice.appendChild(soldOut);
        }
        choice.__ksInlineVariant = variant;
        refs.choices.push(choice); fieldset.appendChild(choice);
      });
    }
    var singleLabel = card.querySelector('[data-ks-inline-variant-label]');
    var singleTitle = product.variants.length === 1 ? product.variants[0].title : '';
    setInlineHidden(singleLabel, !singleTitle || singleTitle === 'Default Title');
    putText(singleLabel, singleTitle && singleTitle !== 'Default Title' ? singleTitle.split(' / ').pop() : '');
    if (refs.control) refs.control.value = product.selectedVariantId;
    if (refs.button) refs.button.dataset.variantId = product.selectedVariantId;
    product.card = card; inline.cards.push(card); fragment.appendChild(card);
    return card;
  }
  function syncInlineCard(product) {
    var card = product.card, refs = card && card.__ksInlineRefs;
    if (!refs || card.__ksInlineVerifiedSerial === inline.serial) return;
    if (refs.control) refs.control.value = product.selectedVariantId;
    refs.choices.forEach(function (choice) {
      var allowed = !!inline.offers[String(choice.dataset.variantId)];
      setInlineHidden(choice, !allowed);
      setInlineAttribute(choice, 'aria-pressed', String(choice.dataset.variantId) === product.selectedVariantId);
    });
    var selected = product.variants.find(function (variant) { return String(variant.variantId) === product.selectedVariantId; });
    if (selected && product.variants.length > 1) updateInlineVariant(card, {dataset: selected});
    card.__ksInlineVerifiedSerial = inline.serial;
  }
  function materializeInlineCards(section) {
    var panel = section.querySelector('#ksPickerOptions');
    if (!panel) return;
    var fragment = document.createDocumentFragment();
    inline.eligible.slice(0, inline.shown).forEach(function (product) { mountInlineCard(product, fragment); });
    if (fragment.childNodes.length) panel.appendChild(fragment);
    // Cards already seen stay reusable when Show fewer is selected.
    inline.cards.forEach(function (card) { syncInlineCard(card.__ksInlineProduct); });
    inline.displayed = inline.cards.filter(function (card) {
      return card.isConnected && card.__ksInlineProduct.eligibleIndex >= 0 && !!inlineOffer(card);
    });
  }
  function renderInline() {
    var root = drawer(), section = root && root.querySelector('[data-ks-inline-chooser]');
    if (!section) return;
    if (inlineCartUpdating()) {
      if (!section.hidden) freezeInlineAction(section);
      return;
    }
    delete section.dataset.ksPendingAction;
    materializeInlineCards(section);
    var chosen = inlineSelections(inline.cart), valid = inline.eligible;
    var hasOptions = valid.length > 0;
    section.dataset.ksEligibilityState = hasOptions ? 'ready' : 'complete';
    section.style.minHeight = '';
    var visible = !inlineCartUpdating() && (hasOptions || !!inline.removeKey || inline.noticeError && !!inline.notice);
    section.hidden = !visible;
    var options = section.querySelector('[data-ks-inline-options]');
    if (options) options.hidden = !hasOptions;
    var allFree = valid.every(function (product) { return inline.offers[product.selectedVariantId].isFree; });
    var count = inline.capacity || 1;
    var heading = inlineText(allFree ? count > 1 ? 'heading_many' : 'heading' : count > 1 ? 'heading_items' : 'heading_item').replace('[count]', String(count));
    putBogoText(section.querySelector('[data-ks-inline-heading]'), heading);
    putText(section.querySelector('[data-ks-inline-copy]'), inlineText('no_cost'));
    inline.cards.forEach(function (card) {
      var product = card.__ksInlineProduct, offer = inlineOffer(card), index = product.eligibleIndex;
      setInlineHidden(card, !offer || index < 0);
      if (index >= inline.shown) setInlineAttribute(card, 'data-ks-picker-collapsed', 'true');
      else if (card.hasAttribute('data-ks-picker-collapsed')) card.removeAttribute('data-ks-picker-collapsed');
      if (card.style.order !== String(index)) card.style.order = String(index);
      var button = card.__ksInlineRefs.button;
      if (button) {
        var disabled = !offer || inlineCartUpdating();
        if (button.disabled !== disabled) button.disabled = disabled;
        if (!state.busy) putText(button, inlineText(offer && offer.isFree ? 'add_free' : 'add_item'));
        if (offer) setInlineAttribute(button, 'aria-label', inlineText(offer.isFree ? 'add_free' : 'add_item') + ': ' + card.dataset.productTitle);
      }
    });
    var more = section.querySelector('[data-ks-picker-toggle]');
    if (more) {
      more.hidden = valid.length <= 4;
      more.disabled = inlineCartUpdating();
      delete more.dataset.loading;
      more.setAttribute('aria-busy', 'false');
      more.setAttribute('aria-expanded', String(inline.shown > 4));
      putText(more, inline.shown >= valid.length ? more.dataset.showFewerLabel : more.dataset.viewAllLabel);
    }
    var confirmation = section.querySelector('[data-ks-inline-confirmation]');
    if (confirmation) {
      confirmation.hidden = !inline.removeKey;
      putText(section.querySelector('[data-ks-inline-confirmation-copy]'), '');
      putText(section.querySelector('[data-ks-change-choice]'), inlineText('cancel'));
    }
    var status = section.querySelector('[data-ks-inline-status]');
    if (status) {
      var statusText = inline.noticeError ? inline.notice : !hasOptions && inline.removeKey ? inlineText('unavailable') : '';
      status.dataset.loading = 'false';
      putBogoText(status, statusText);
      status.classList.toggle('is-error', inline.noticeError);
    }
    root.querySelectorAll('[data-ks-recommendations]').forEach(function (node) { node.hidden = visible || chosen.length > 0; });
    restoreInlineView();
    if (hasOptions) scheduleInlineExposure();
  }
  function updateInlineVariant(card, choice) {
    if (!card || !choice) return;
    var data = choice.dataset;
    var product = card.__ksInlineProduct;
    if (product) {
      product.selectedVariantId = String(data.variantId || product.selectedVariantId);
      product.dataset.optionPrice = data.price || product.dataset.optionPrice;
      product.dataset.optionFullPrice = data.fullPrice || product.dataset.optionFullPrice;
      product.dataset.optionOriginalPrice = data.originalPrice || product.dataset.optionOriginalPrice;
    }
    card.dataset.optionPrice = data.price || card.dataset.optionPrice;
    card.dataset.optionFullPrice = data.fullPrice || card.dataset.optionFullPrice;
    card.dataset.optionOriginalPrice = data.originalPrice || card.dataset.optionOriginalPrice;
    if (data.contents) putText(card.__ksInlineRefs ? card.__ksInlineRefs.included : card.querySelector('.ks-cart-option__included'), data.contents.trim());
    var thumbnail = card.__ksInlineRefs ? card.__ksInlineRefs.image : card.querySelector('img');
    if (thumbnail && data.imageSrc) {
      if (data.imageSrcset) {
        if (thumbnail.getAttribute('srcset') !== data.imageSrcset) thumbnail.setAttribute('srcset', data.imageSrcset);
      } else thumbnail.removeAttribute('srcset');
      if (thumbnail.getAttribute('src') !== data.imageSrc) thumbnail.setAttribute('src', data.imageSrc);
    }
  }
  function verifyInlineProduct(product, session, serial) {
    product.eligibleIndex = -1;
    var valid = [];
    for (var i = 0; i < product.variants.length; i += 1) {
      if (serial !== inline.serial || inlineCartUpdating()) return;
      var variant = product.variants[i], variantId = String(variant.variantId);
      var offer = variant.available === false ? null : session.quote(Number(variantId));
      if (offer && offer.allowed === true) { inline.offers[variantId] = offer; valid.push(variant); }
    }
    if (!valid.length) return;
    var selected = valid.find(function (variant) { return String(variant.variantId) === product.selectedVariantId; }) || valid[0];
    product.selectedVariantId = String(selected.variantId);
    product.dataset.optionPrice = selected.price || product.dataset.optionPrice;
    product.dataset.optionFullPrice = selected.fullPrice || product.dataset.optionFullPrice;
    product.dataset.optionOriginalPrice = selected.originalPrice || product.dataset.optionOriginalPrice;
    product.eligibleIndex = inline.eligible.length;
    inline.eligible.push(product);
  }
  function fillInline(serial) {
    if (!inline.session || serial !== inline.serial || inlineCartUpdating()) return;
    var session = inline.session;
    // Eligibility still covers every variant using the immutable local catalog.
    // Only the requested batch becomes product-card DOM; no requests are added.
    inline.eligible = [];
    inline.products.forEach(function (product) { verifyInlineProduct(product, session, serial); });
    if (serial !== inline.serial || inlineCartUpdating()) return;
    inline.cursor = inline.products.length;
    inline.loading = false; inline.filling = false;
    inline.capacity = inline.eligible.length ? session.capacity(inline.offers[inline.eligible[0].selectedVariantId]) || 1 : 1;
    renderInline();
  }
  function startInline(force) {
    if (inlineCartUpdating()) return;
    settleInlineMarkup();
    var root = drawer(), section = root && root.querySelector('[data-ks-inline-chooser]');
    if (!section) { restoreInlineView(); return; }
    var dataNode = root.querySelector('[data-ks-inline-cart-state]');
    var catalogNode = root.querySelector('[data-ks-inline-catalog]');
    var productsNode = section.querySelector('[data-ks-inline-products]');
    var templateNode = section.querySelector('[data-ks-inline-card-template]');
    var quotes = window.KinsoraCartQuotes;
    if (!quotes) { inline.node = null; inline.session = null; inline.offers = {}; inline.displayed = []; inline.eligible = []; renderInline(); return; }
    var cardsConnected = inline.cards.every(function (card) { return card.isConnected; });
    if (!force && inline.node === dataNode && inline.catalogNode === catalogNode
      && inline.productsNode === productsNode && inline.templateNode === templateNode
      && inline.productsSection === section && cardsConnected) { restoreInlineView(); return; }
    inline.node = dataNode; inline.catalogNode = catalogNode; inline.cart = inlineCurrentCart(); inline.catalog = inlineCatalog();
    if (inline.notice && quotes && inline.noticeFingerprint !== quotes.fingerprint(inline.cart)) { inline.notice = ''; inline.noticeError = false; }
    var serial = ++inline.serial;
    inline.session = null; inline.offers = {}; inline.displayed = []; inline.cursor = 0; inline.shown = 4;
    inline.loading = false; inline.filling = false; inline.capacity = 1;
    if (!inline.productsLoaded || inline.productsNode !== productsNode || inline.templateNode !== templateNode
      || inline.productsSection !== section || !cardsConnected) {
      if (productsNode) inline.cards.forEach(function (card) { if (card.isConnected) card.remove(); });
      loadInlineProducts(productsNode, templateNode, section);
    }
    inline.eligible = [];
    inline.products.forEach(function (product) { product.eligibleIndex = -1; });
    rankInlineProducts(root);
    if (!inline.cart || !inline.cart.items || !inline.cart.items.length) { renderInline(); return; }
    if (inline.removeKey && !inline.cart.items.some(function (item) { return item.key === inline.removeKey; })) inline.removeKey = '';
    try {
      inline.session = quotes.create(inline.cart, {catalog: inline.catalog, country: root.dataset.ksCountry,
        customerContext: root.dataset.ksCustomerContext === 'true', removeKey: inline.removeKey || undefined,
        giftThresholdCents: Number(root.dataset.giftThresholdBaseCents)});
      fillInline(serial);
    } catch (error) {
      inline.session = null; inline.offers = {}; inline.displayed = []; inline.eligible = []; renderInline();
    }
  }
  function chooseInlineReplacement(key) {
    if (inlineCartUpdating()) return;
    inline.removeKey = key || ''; inline.notice = ''; inline.noticeError = false;
    startInline(true);
    var heading = drawer() && drawer().querySelector('[data-ks-inline-heading]');
    if (heading && inline.removeKey) heading.scrollIntoView({block: 'nearest'});
  }
  async function addInlineChoice(button) {
    var card = button.closest('[data-ks-inline-card]'), selection = optionSelection(card), shown = inlineOffer(card);
    if (!shown || inlineCartUpdating() || button.disabled) return;
    var root = drawer(), removeKey = inline.removeKey;
    inline.notice = ''; inline.noticeError = false;
    var prepared = null, didWrite = false, removalDone = false;
    inline.pendingControl = button;
    button.dataset.ksBusyLabel = inlineText(removeKey ? 'changing' : 'adding');
    announce(button.dataset.ksBusyLabel);
    var result = await runMutation(button, async function () {
      var baseline = await fetchJson('/cart.js');
      var engine = window.KinsoraCartQuotes;
      if (!engine || engine.fingerprint(baseline) !== shown.sourceFingerprint) throw new Error('Cart changed before the selection.');
      var session = await window.KinsoraCartQuotes.create(baseline, {
        catalog: inline.catalog, country: root.dataset.ksCountry,
        customerContext: root.dataset.ksCustomerContext === 'true', removeKey: removeKey || undefined,
        giftThresholdCents: Number(root.dataset.giftThresholdBaseCents)
      });
      prepared = await session.quote(selection.variantId);
      if (!prepared.allowed) throw new Error('Selection is no longer available at no extra cost.');
      var marker = prepared.items[0].properties._kinsora_cart_unit;
      function added(cart) { return cart.items.find(function (item) { return (item.properties || {})._kinsora_cart_unit === marker; }); }
      function source(cart) {
        var original = prepared.baselineCart.items.find(function (item) { return item.key === removeKey; });
        return original && cart.items.find(function (item) { return engine.ajaxKey(item) === engine.ajaxKey(original); });
      }
      async function restore() {
        async function recoverWrite(path, body) {
          try { await fetchJson(path, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}); }
          catch (error) { if (error.kinsoraCartRequestTimeout) throw error; /* Read before any next write. */ }
          return fetchJson('/cart.js');
        }
        try {
          var cart = await fetchJson('/cart.js'), newLine = added(cart);
          if (newLine) {
            cart = await recoverWrite('/cart/change.js', {id:newLine.key,quantity:0});
            if (added(cart)) throw new Error('Added selection is still present.');
          }
          if (prepared.removal && removalDone) {
            var original = prepared.baselineCart.items.find(function (item) { return item.key === removeKey; }), current = source(cart);
            if (current && Number(current.quantity) !== Number(original.quantity)) {
              cart = await recoverWrite('/cart/change.js', {id:current.key,quantity:original.quantity});
            } else if (!current) {
              var item = {id:original.variant_id,quantity:original.quantity,properties:original.properties || {}};
              if (original.selling_plan_allocation) item.selling_plan = original.selling_plan_allocation.selling_plan.id;
              cart = await recoverWrite('/cart/add.js', {items:[item]});
            }
          }
          cart = await fetchJson('/cart.js');
          if (!engine.rollbackMatches(prepared.baselineCart, cart)) throw new Error('Original cart differs.');
          return cart;
        } catch (error) {
          var unsafe = new Error('Cart restoration could not be confirmed.');
          unsafe.kinsoraUnsafeCartState = true;
          throw unsafe;
        }
      }
      try {
        didWrite = true;
        var cart = null;
        try { await fetchJson('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:prepared.items})}); }
        catch (error) { if (error.kinsoraCartRequestTimeout) throw error; cart = await fetchJson('/cart.js'); if (!added(cart)) throw error; }
        if (!cart) cart = await fetchJson('/cart.js');
        if (prepared.removal) {
          var old = source(cart); if (!old) throw new Error('Original selection was not found.');
          removalDone = true;
          try { cart = await fetchJson('/cart/change.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:old.key,quantity:prepared.removal.quantity})}); }
          catch (error) { if (error.kinsoraCartRequestTimeout) throw error; cart = await fetchJson('/cart.js'); }
        }
        if (!window.KinsoraCartQuotes.matches(prepared, cart)) throw new Error('Shopify returned a different selection.');
        inline.removeKey = '';
        return cart;
      } catch (error) {
        if (error.kinsoraCartRequestTimeout) {
          if (didWrite) error.kinsoraUnsafeCartState = true;
          throw error;
        }
        if (didWrite) await restore();
        throw error;
      }
    }, '', {inlineChoice: true});
    releaseInlineAction();
    inline.pendingControl = null;
    delete button.dataset.ksBusyLabel;
    if (result && prepared && window.KinsoraCartQuotes.matches(prepared, result)) {
      var item = result.items.find(function (line) { return (line.properties || {})._kinsora_cart_unit === prepared.items[0].properties._kinsora_cart_unit; });
      inline.notice = inlineText(item && Number(item.final_line_price) === 0 ? 'added_free' : 'added_item');
      inline.noticeError = false;
      inlineEvent('chooser_selection', {variant_id: selection.variantId, action: removeKey ? 'change' : 'add', free: !!item && Number(item.final_line_price) === 0});
      announce(inline.notice);
    } else {
      inline.notice = inlineText('error'); inline.noticeError = true; announce(inline.notice);
    }
    inline.noticeFingerprint = window.KinsoraCartQuotes.fingerprint(inlineCurrentCart());
    inline.node = null;
    startInline(true);
  }

  function currentCartLines() {
    var root = drawer();
    if (!root) return [];
    var lines = [];
    root.querySelectorAll('[data-ks-cart-line][data-bogo-eligible="true"]').forEach(function (line) {
      var quantity = Math.max(1, parseInt(line.dataset.lineQuantity || '1', 10) || 1);
      var price = Number(line.dataset.eligiblePrice || line.dataset.lineOriginal || 0);
      var freeUnits = Math.max(0, Math.min(quantity, parseInt(line.dataset.bogoFreeUnits || '0', 10) || 0));
      for (var i = 0; i < quantity; i += 1) {
        lines.push({
          id: line.dataset.cartItemKey + ':' + i,
          title: line.dataset.productTitleFull || line.dataset.productTitle || 'Cart item',
          displayName: line.dataset.displayName || line.dataset.productTitle || 'Cart item',
          price: price,
          candidate: false,
          isFree: i >= quantity - freeUnits,
          order: lines.length
        });
      }
    });
    return lines;
  }

  function calculateBogoProjection(existingLines, selected, currentTotal, currentSavings) {
    var lines = (Array.isArray(existingLines) ? existingLines : []).map(function (line, index) {
      if (typeof line === 'number') {
        return { id: 'line-' + index, title: 'Cart item', displayName: 'Cart item', price: line, candidate: false, isFree: false, order: index };
      }
      return {
        id: line.id || ('line-' + index),
        title: line.title || line.displayName || 'Cart item',
        displayName: line.displayName || line.title || 'Cart item',
        price: Math.max(0, Number(line.price) || 0),
        candidate: false,
        isFree: !!line.isFree,
        order: index
      };
    }).filter(function (line) { return line.price > 0; });
    var selectedLine = {
      id: 'candidate',
      title: selected.title || selected.displayName || 'Selected item',
      displayName: selected.displayName || selected.title || 'Selected item',
      price: Math.max(0, Number(selected.price) || 0),
      candidate: true,
      isFree: false,
      order: lines.length
    };
    var currentFreeCount = lines.filter(function (line) { return line.isFree; }).length;
    var projectedLines = lines.concat(selectedLine);
    var projectedFreeCount = Math.floor(projectedLines.length / 3) * 2;
    var projectedFreeLines = projectedLines.slice().sort(function (a, b) {
      if (a.price !== b.price) return a.price - b.price;
      if (a.isFree !== b.isFree) return a.isFree ? -1 : 1;
      if (a.candidate !== b.candidate) return a.candidate ? 1 : -1;
      return a.order - b.order;
    }).slice(0, projectedFreeCount);
    var projectedSavings = projectedFreeLines.reduce(function (sum, line) { return sum + line.price; }, 0);
    var actualCurrentSavings = Math.max(0, Number(currentSavings) || lines.filter(function (line) { return line.isFree; }).reduce(function (sum, line) { return sum + line.price; }, 0));
    // Savings can move backwards when a newly added cheaper item becomes the
    // free line. Keep that signed delta: clamping it to zero understates the
    // amount added to the cart (for example, $60/$60 + $25 rises by $60).
    var savingsDelta = projectedSavings - actualCurrentSavings;
    var incrementalSavings = Math.max(0, savingsDelta);
    var incrementalCost = Math.max(0, selectedLine.price - savingsDelta);
    var newlyFreeLine = projectedFreeLines.filter(function (line) { return !line.isFree; })[0] || null;
    var projectedFreeIds = projectedFreeLines.map(function (line) { return line.id; });
    var displacedFreeLines = lines.filter(function (line) {
      return line.isFree && projectedFreeIds.indexOf(line.id) === -1;
    });
    return {
      currentFreeCount: currentFreeCount,
      projectedFreeCount: projectedFreeCount,
      expectedSavings: projectedSavings,
      savingsDelta: savingsDelta,
      incrementalSavings: incrementalSavings,
      incrementalCost: incrementalCost,
      projectedTotal: Math.max(0, Number(currentTotal || 0) + incrementalCost),
      completesPair: projectedFreeCount > currentFreeCount,
      addsAtNoCost: incrementalCost === 0,
      freeLine: newlyFreeLine,
      displacedFreeLines: displacedFreeLines,
      projectedFreeLines: projectedFreeLines
    };
  }

  function optionSelection(card) {
    var control = card && card.querySelector('[data-ks-option-variant]');
    var option = control && control.tagName === 'SELECT' ? control.options[control.selectedIndex] : null;
    var price = option ? Number(option.dataset.price || card.dataset.optionPrice || 0) : Number(card.dataset.optionPrice || 0);
    var fullPrice = option ? Number(option.dataset.fullPrice || card.dataset.optionFullPrice || price) : Number(card.dataset.optionFullPrice || price);
    var originalPrice = option ? Number(option.dataset.originalPrice || card.dataset.optionOriginalPrice || price) : Number(card.dataset.optionOriginalPrice || price);
    return {
      variantId: Number(control && control.value),
      price: price,
      fullPrice: Math.max(fullPrice, price),
      originalPrice: originalPrice,
      title: card && card.dataset.productTitle || 'Product',
      displayName: card && card.dataset.displayName || card && card.dataset.productTitle || 'Product',
      includedItems: card && card.dataset.includedItems || '',
      kind: card && card.dataset.productKind === 'set' ? 'set' : 'item'
    };
  }

  function projectionFor(card, selection) {
    var root = drawer();
    var selected = selection || optionSelection(card);
    var currentTotal = Number(root && root.dataset.currentTotal || 0);
    var currentSavings = Number(root && root.dataset.currentBogoSavings || 0);
    var projection = calculateBogoProjection(currentCartLines(), selected, currentTotal, currentSavings);
    projection.selected = selected;
    return projection;
  }

  function selectedItemIsFree(projection) {
    if (!projection || !projection.selected || !projection.freeLine) return false;
    return projection.completesPair && projection.addsAtNoCost &&
      (projection.freeLine.candidate || projection.freeLine.price === projection.selected.price);
  }

  function pickerSelectionAllowed(card, selection) {
    if (!selection.variantId || selection.price <= 0) return false;
    if (state.mode === 'change') {
      return state.paidPrice > 0 && selection.price <= state.paidPrice;
    }
    return state.paidPrice > 0 && selection.price <= state.paidPrice &&
      selectedItemIsFree(projectionFor(card, selection));
  }

  function filterPickerVariants(card) {
    var control = card.querySelector('[data-ks-option-variant]');
    var selection = optionSelection(card);
    var choices = Array.prototype.slice.call(card.querySelectorAll('[data-ks-variant-choice]'));
    if (!choices.length) return pickerSelectionAllowed(card, selection);
    var available = choices.filter(function (choice) {
      var candidate = Object.assign({}, selection, {
        variantId: Number(choice.dataset.variantId || 0),
        price: Number(choice.dataset.price || 0)
      });
      choice.hidden = choice.disabled || !pickerSelectionAllowed(card, candidate);
      return !choice.hidden;
    });
    var selectedChoice = available.filter(function (choice) {
      return Number(choice.dataset.variantId) === selection.variantId;
    })[0] || available[0];
    if (!selectedChoice || !control) return false;
    control.value = selectedChoice.dataset.variantId;
    card.dataset.optionPrice = selectedChoice.dataset.price;
    card.dataset.optionFullPrice = selectedChoice.dataset.fullPrice;
    card.dataset.optionOriginalPrice = selectedChoice.dataset.originalPrice;
    choices.forEach(function (choice) {
      choice.setAttribute('aria-pressed', choice === selectedChoice ? 'true' : 'false');
    });
    return true;
  }

  function renderOptionPrice(card, projection) {
    if (!card || !projection || !projection.selected || card.classList.contains('ks-cart-option--recommendation')) return;
    var priceRow = card.querySelector('.ks-cart-option__price-row');
    if (!priceRow) return;
    var selection = projection.selected;
    var isFree = selectedItemIsFree(projection);
    priceRow.textContent = '';
    var price = document.createElement('strong');
    if (isFree) {
      price.className = 'ks-cart-option__free-price';
      putBogoText(price, projection.freeLine.candidate ? 'FREE' : 'No extra cost');
    } else {
      price.textContent = money(selection.price);
    }
    priceRow.appendChild(price);
    if (!isFree && selection.fullPrice > selection.price) {
      var retail = document.createElement('s');
      retail.textContent = money(selection.fullPrice);
      var saving = document.createElement('span');
      saving.textContent = 'Save ' + money(selection.fullPrice - selection.price);
      priceRow.appendChild(retail);
      priceRow.appendChild(saving);
    }
  }

  function renderOptionOutcome(card) {
    if (!card || card.hidden || card.hasAttribute('data-ks-inline-card')) return;
    var outcome = card.querySelector('[data-ks-option-outcome]');
    var button = card.querySelector('[data-ks-add-product]');
    if (!outcome || !button || button.disabled) return;
    var projection = projectionFor(card);
    renderOptionPrice(card, projection);
    outcome.textContent = '';

    if (card.classList.contains('ks-cart-option--recommendation')) {
      outcome.hidden = true;
      var recommendationLabel = card.dataset.productKind === 'set' ? '+ Add set' : '+ Add item';
      var recommendationVariantId = String(projection.selected.variantId || '');
      if (button.textContent !== recommendationLabel) button.textContent = recommendationLabel;
      if (button.dataset.variantId !== recommendationVariantId) button.dataset.variantId = recommendationVariantId;
      return;
    }
    outcome.hidden = false;

    if (state.mode === 'change') {
      var strong = document.createElement('strong');
      var detail = document.createElement('span');
      putBogoText(strong, 'FREE');
      detail.textContent = 'Cart total unchanged';
      button.textContent = 'Select';
      button.setAttribute('aria-label', 'Select ' + card.dataset.productTitle + ' as your FREE BOGO item');
      outcome.appendChild(strong);
      outcome.appendChild(detail);
    } else {
      var itemKind = projection.selected.kind === 'set' ? 'set' : 'item';
      var result = document.createElement('span');
      result.className = 'ks-cart-option__bogo-result';
      var increment = document.createElement('strong');
      increment.className = 'ks-cart-option__increment';
      var total = document.createElement('span');
      total.className = 'ks-cart-option__new-total';
      var totalLabel = document.createElement('span');
      totalLabel.textContent = 'New cart total';
      var totalPrice = document.createElement('b');
      totalPrice.textContent = money(projection.projectedTotal);
      total.appendChild(totalLabel);
      total.appendChild(totalPrice);

      if (selectedItemIsFree(projection)) {
        var equalPriceTie = !projection.freeLine.candidate;
        totalLabel.textContent = equalPriceTie ? 'Added cost' : 'Your price';
        totalPrice.textContent = equalPriceTie ? money(0) : 'FREE';
        totalPrice.className = 'ks-cart-option__free-total';
        result.textContent = equalPriceTie ? 'BOGO may make an equal-priced item already in your cart FREE.' : 'This item is FREE with BOGO.';
        if (projection.displacedFreeLines.length) {
          increment.textContent = projection.displacedFreeLines[0].displayName + ' returns to its paid price; your cart increases ' + money(projection.incrementalCost) + '.';
        } else {
          increment.textContent = 'Your cart total stays ' + money(projection.projectedTotal) + '.';
          if (equalPriceTie) increment.textContent += ' An equal-priced item in your cart may become FREE.';
        }
        button.textContent = equalPriceTie ? 'Add at no extra cost' : 'Add FREE item';
      } else if (!projection.completesPair || !projection.freeLine) {
        result.textContent = 'Add one more eligible item to unlock the next BOGO discount.';
        increment.textContent = 'This ' + itemKind + ' adds ' + money(projection.incrementalCost) + '.';
        button.textContent = 'Add ' + itemKind;
      } else {
        result.textContent = 'Your ' + money(projection.freeLine.price) + ' ' + projection.freeLine.displayName + ' becomes FREE.';
        increment.textContent = 'You pay ' + money(projection.incrementalCost) + ' for this ' + itemKind + '.';
        button.textContent = 'Add & apply BOGO';
      }
      putBogoText(result, result.textContent);
      putBogoText(increment, increment.textContent);
      putBogoText(totalPrice, totalPrice.textContent);
      outcome.appendChild(result);
      outcome.appendChild(increment);
      outcome.appendChild(total);
      button.setAttribute(
        'aria-label',
        'Add ' + projection.selected.displayName +
          '; ' + result.textContent +
          '; pay ' + money(projection.incrementalCost) + ' more' +
          '; new cart total ' + money(projection.projectedTotal)
      );
    }
    button.dataset.variantId = String(projection.selected.variantId || '');
  }

  function updatePickerDisclosure(panel, resetExpanded) {
    var root = drawer();
    if (!root || !panel) return 0;
    var toggle = root.querySelector('[data-ks-picker-toggle="' + selectorEscape(panel.dataset.ksPickerPanel) + '"]');
    var compatibleCards = Array.prototype.filter.call(panel.querySelectorAll('[data-ks-option]'), function (card) {
      return !card.hidden;
    });
    if (resetExpanded || compatibleCards.length <= 4) panel.dataset.ksPickerExpanded = 'false';
    var expanded = panel.dataset.ksPickerExpanded === 'true';
    panel.querySelectorAll('[data-ks-option]').forEach(function (card) {
      if (!expanded && compatibleCards.indexOf(card) >= 4) card.dataset.ksPickerCollapsed = 'true';
      else card.removeAttribute('data-ks-picker-collapsed');
    });
    if (toggle) {
      toggle.hidden = panel.hidden || compatibleCards.length <= 4;
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      var label = expanded ? toggle.dataset.showFewerLabel : toggle.dataset.viewAllLabel;
      var text = String(label || '').replace(/\[count\]/g, String(compatibleCards.length));
      if (toggle.textContent !== text) toggle.textContent = text;
    }
    return compatibleCards.length;
  }

  function populatePickerCart(container, currentLine, fallbackTitle, fallbackPrice) {
    if (!container) return;
    var root = drawer();
    container.textContent = '';
    if (!root) return;
    var currentTotal = Number(root.dataset.currentTotal || 0);
    var completedCount = Math.max(0, Number(root.dataset.bogoFreeCount || state.completedPairCount || 0));
    var readyName = currentLine && (currentLine.dataset.displayName || currentLine.dataset.productTitle) || fallbackTitle || 'Eligible cart item';
    var readyPrice = currentLine && Number(currentLine.dataset.eligiblePrice || currentLine.dataset.lineFinal || 0) || Number(fallbackPrice || 0);

    var heading = document.createElement('strong');
    heading.className = 'ks-picker-cart__summary-heading';
    heading.textContent = 'YOUR CART · ' + money(currentTotal);

    var applied = document.createElement('span');
    applied.className = 'ks-picker-cart__applied';
    putBogoText(applied, completedCount > 0
      ? '✓ ' + completedCount + ' FREE item' + (completedCount === 1 ? '' : 's') + ' included with Buy 1, Get 1'
      : 'Your FREE item is waiting');

    var ready = document.createElement('span');
    ready.className = 'ks-picker-cart__ready';
    var readyLabel = document.createElement('strong');
    readyLabel.textContent = 'Eligible item in your cart:';
    var readyValue = document.createElement('span');
    readyValue.textContent = readyName + ' · ' + money(readyPrice);
    ready.appendChild(readyLabel);
    ready.appendChild(readyValue);

    container.appendChild(heading);
    container.appendChild(applied);
    container.appendChild(ready);
    container.setAttribute('aria-label', 'Your cart total is ' + money(currentTotal) + '. ' + (completedCount > 0 ? completedCount + ' FREE item' + (completedCount === 1 ? '' : 's') + ' included with Buy 1, Get 1. ' : 'Your FREE item is waiting. ') + 'Eligible item in your cart: ' + readyName + ', ' + money(readyPrice) + '.');
  }

  function reconcileRewardPresentation(root) {
    var reward = root && root.querySelector('.ks-reward[data-reward-milestone-template]');
    if (!reward || reward.hidden) return;
    var progress = reward.querySelector('.ks-reward__progress');
    if (!progress) return;
    // The existing cart synchronizer refreshes these values from its cart
    // snapshot. Use that threshold while retaining its currency safeguards.
    var threshold = Number(progress.getAttribute('aria-valuemax'));
    var qualifyingSubtotal = Number(progress.getAttribute('aria-valuenow'));
    if (!isFinite(threshold) || threshold <= 0 || !isFinite(qualifyingSubtotal) || qualifyingSubtotal < 0) return;
    var reached = qualifyingSubtotal >= threshold;
    var giftsVerified = root.dataset.pouchPresent === 'true' &&
      (root.dataset.enableFreeBracelet !== 'true' || root.dataset.braceletPresent === 'true');
    var unlocked = reached && giftsVerified;
    var thresholdMoney = money(threshold).replace(/\.00(?=\D*$)/, '');
    var remainingMoney = money(Math.max(0, threshold - qualifyingSubtotal));
    var milestone = unlocked ? reward.dataset.rewardUnlockedCopy : reached
      ? reward.dataset.rewardUnlockingCopy
      : String(reward.dataset.rewardMilestoneTemplate || '').replace(/\[amount\]/g, thresholdMoney);
    var headlineCopy = unlocked ? reward.dataset.rewardAddedCopy : reached
      ? reward.dataset.rewardAddingCopy
      : String(reward.dataset.rewardHeadlineTemplate || '').replace(/\[amount\]/g, remainingMoney);
    var shippingCopy = reward.dataset.rewardShippingCopy || '';
    var headline = reward.querySelector('.ks-reward__headline');
    if (headline && headlineCopy && headline.textContent.trim() !== headlineCopy) {
      var currentAccent = headline.querySelector('.ks-reward__accent');
      var accentCopy = !reached ? remainingMoney : currentAccent && currentAccent.textContent;
      var accentIndex = accentCopy ? headlineCopy.indexOf(accentCopy) : -1;
      if (accentIndex >= 0) {
        headline.textContent = '';
        var accent = document.createElement('span');
        accent.className = 'ks-reward__accent';
        accent.textContent = accentCopy;
        headline.appendChild(document.createTextNode(headlineCopy.slice(0, accentIndex)));
        headline.appendChild(accent);
        headline.appendChild(document.createTextNode(headlineCopy.slice(accentIndex + accentCopy.length)));
      } else {
        headline.textContent = headlineCopy;
      }
    }
    var fill = reward.querySelector('.ks-reward__rail > span');
    var fillWidth = (unlocked ? 100 : Math.min(75, 25 + qualifyingSubtotal / threshold * 50)) + '%';
    if (fill && fill.style.width !== fillWidth) fill.style.width = fillWidth;
    var giftDot = reward.querySelector('.ks-reward__dot--pouch');
    if (giftDot) giftDot.classList.toggle('is-earned', unlocked);
    var giftLabel = reward.querySelector('.ks-reward__label--pouch');
    if (giftLabel && milestone && giftLabel.textContent.trim() !== milestone) giftLabel.textContent = milestone;
    var shippingLabel = reward.querySelector('.ks-reward__label--shipping');
    if (shippingLabel && shippingCopy && shippingLabel.textContent.trim() !== shippingCopy) shippingLabel.textContent = shippingCopy;
    var accessibleLabel = shippingCopy + '. ' + milestone;
    if (milestone && progress.getAttribute('aria-label') !== accessibleLabel) progress.setAttribute('aria-label', accessibleLabel);
    reward.classList.toggle('ks-reward--earned', unlocked);
    reward.classList.toggle('ks-reward--locked', !unlocked);
  }

  function hydrateOptions(resetPickerPosition) {
    var root = drawer();
    if (!root) return;
    startInline();
    reconcileRewardPresentation(root);
    reconcilePendingBogoStatus(root);
    var configuredGiftId = Number(root.dataset.freeGiftVariantId || 0);
    if (configuredGiftId) {
      window.Kinsora = window.Kinsora || {};
      window.Kinsora.freeGiftVariantIds = [configuredGiftId];
      window.Kinsora.freeGiftAutoVariantId = configuredGiftId;
    }
    var handles = {};
    root.querySelectorAll('[data-ks-cart-line]').forEach(function (line) {
      if (line.dataset.productHandle) handles[line.dataset.productHandle] = true;
    });
    root.querySelectorAll('[data-ks-option]').forEach(function (card) {
      if (card.hasAttribute('data-ks-inline-card')) return;
      var inCart = handles[card.dataset.productHandle];
      if (card.closest('[data-ks-sheet="picker"]')) {
        var hasFreeOption = filterPickerVariants(card);
        card.hidden = !hasFreeOption;
      } else {
        card.hidden = !!inCart;
      }
      renderOptionOutcome(card);
    });
    var giftNotice = root.querySelector('[data-ks-gift-notice]');
    if (giftNotice) {
      var noticeState = window.Kinsora && window.Kinsora.freeGiftNotice;
      if (!noticeState && window.Kinsora && window.Kinsora.freeGiftDiscountError) {
        noticeState = { type: 'error' };
      }
      var noticeTitle = giftNotice.querySelector('[data-ks-gift-notice-title]');
      var noticeCopy = giftNotice.querySelector('[data-ks-gift-notice-copy]');
      giftNotice.hidden = !noticeState;
      giftNotice.classList.toggle('is-error', !!noticeState && noticeState.type === 'error');
      if (noticeState && noticeState.type === 'threshold') {
        if (noticeTitle && noticeTitle.textContent !== 'Bonus pouch and bracelet removed') noticeTitle.textContent = 'Bonus pouch and bracelet removed';
        var thresholdNoticeCopy = 'Add ' + money(noticeState.remainingCents) + ' more to unlock them again.';
        if (noticeCopy && noticeCopy.textContent !== thresholdNoticeCopy) noticeCopy.textContent = thresholdNoticeCopy;
      } else if (noticeState && noticeState.type === 'error') {
        if (noticeTitle && noticeTitle.textContent !== 'Bonus gifts unavailable') noticeTitle.textContent = 'Bonus gifts unavailable';
        if (noticeCopy && noticeCopy.textContent !== 'They were not discounted to $0, so we removed them. You were not charged.') noticeCopy.textContent = 'They were not discounted to $0, so we removed them. You were not charged.';
      }
    }
    var bogoNotice = root.querySelector('[data-ks-bogo-notice]');
    if (bogoNotice) {
      var bogoNoticeState = window.Kinsora && window.Kinsora.bogoDiscountNotice;
      var bogoNoticeTitle = bogoNotice.querySelector('[data-ks-bogo-notice-title]');
      var bogoNoticeCopy = bogoNotice.querySelector('[data-ks-bogo-notice-copy]');
      bogoNotice.hidden = !bogoNoticeState;
      var bogoNoticeTitleText = bogoNoticeState ? 'BOGO item removed' : '';
      var bogoNoticeCopyText = bogoNoticeState && bogoNoticeState.text || '';
      if (bogoNoticeTitle && bogoNoticeTitle.textContent !== bogoNoticeTitleText) bogoNoticeTitle.textContent = bogoNoticeTitleText;
      if (bogoNoticeCopy && bogoNoticeCopy.textContent !== bogoNoticeCopyText) bogoNoticeCopy.textContent = bogoNoticeCopyText;
    }
    root.querySelectorAll('[data-ks-recommendations]').forEach(function (section) {
      var visible = section.querySelector('[data-ks-option]:not([hidden])');
      section.hidden = !visible;
    });
    var freeOptionCount = 0;
    root.querySelectorAll('[data-ks-picker-panel]').forEach(function (panel) {
      if (panel.closest('[data-ks-inline-chooser]')) return;
      panel.hidden = false;
      freeOptionCount += updatePickerDisclosure(panel, resetPickerPosition === true);
    });
    var picker = root.querySelector('[data-ks-sheet="picker"]');
    if (picker && !picker.hidden) {
      var emptyMessage = 'No FREE options are available for this item right now.';
      var pickerStatus = picker.querySelector('[data-ks-sheet-status]');
      if (!freeOptionCount) sheetStatus(picker, emptyMessage);
      else if (pickerStatus && pickerStatus.textContent === emptyMessage) sheetStatus(picker, '');
    }
  }

  function configurePicker(trigger, catalogRefreshed) {
    if (drawer() && drawer().querySelector('[data-ks-inline-chooser]')) { chooseInlineReplacement(trigger.dataset.freeLineKey || ''); return; }
    var root = drawer();
    if (!catalogRefreshed && root && root.querySelector('[data-ks-picker-catalog-refresh]')) {
      var pickerMode = trigger.dataset.ksOpenPicker;
      setBusy(trigger, true);
      Promise.resolve().then(function () {
        return window.Kinsora.refreshCartDrawer({ strict: true });
      }).then(function () {
        var currentRoot = drawer();
        var currentTrigger = currentRoot && currentRoot.querySelector('[data-ks-open-picker="' + selectorEscape(pickerMode) + '"]');
        if (currentTrigger) configurePicker(currentTrigger, true);
      }).catch(function (error) {
        console.error('[Kinsora cart redesign]', error);
        setBusy(trigger, false);
        announce('Your FREE options could not be loaded. Please try again.');
      });
      return;
    }
    state.mode = trigger.dataset.ksOpenPicker === 'change' ? 'change' : 'add';
    state.pairId = trigger.dataset.pairId || ('bogo-' + Date.now());
    state.oldFreeKey = trigger.dataset.freeLineKey || '';
    var oldFreeLine = state.oldFreeKey && root
      ? root.querySelector('[data-cart-item-key="' + selectorEscape(state.oldFreeKey) + '"]')
      : null;
    state.oldFreeVariantId = Number(oldFreeLine && oldFreeLine.dataset.variantId || 0);
    state.paidPrice = Number(trigger.dataset.paidPrice || 0);

    var sheet = root && root.querySelector('[data-ks-sheet="picker"]');
    if (!sheet) return;
    sheetStatus(sheet, '');
    sheet.dataset.ksPickerMode = state.mode;
    var title = sheet.querySelector('[data-ks-picker-title]');
    var copy = sheet.querySelector('[data-ks-picker-copy]');
    var current = sheet.querySelector('[data-ks-picker-current]');
    var pickerCart = sheet.querySelector('[data-ks-picker-cart]');
    putBogoText(title, 'Choose your FREE item');
    state.completedPairCount = Number(root.dataset.bogoFreeCount || 0);
    if (state.mode === 'change') {
      copy.textContent = 'Choose another eligible product. Your cart total stays the same.';
      current.hidden = true;
      current.textContent = '';
      if (pickerCart) {
        pickerCart.hidden = true;
        pickerCart.textContent = '';
      }
    } else {
      copy.textContent = 'Choose an eligible product. Buy 1, Get 1 savings apply automatically to the lower-priced items.';
      current.hidden = true;
      var currentLine = root.querySelector('[data-cart-item-key="' + selectorEscape(trigger.dataset.currentLineKey || '') + '"]');
      state.paidPrice = Number(currentLine && currentLine.dataset.eligiblePrice || 0);
      current.textContent = '';
      if (pickerCart) {
        populatePickerCart(pickerCart, currentLine, trigger.dataset.currentTitle, trigger.dataset.currentPrice);
        pickerCart.hidden = false;
      }
    }

    openSheet('picker', trigger);
    hydrateOptions(true);
  }

  function addedLineFromCart(cart, variantId, pairId, cartUnit) {
    if (!cart || !cart.items) return null;
    for (var i = cart.items.length - 1; i >= 0; i -= 1) {
      var item = cart.items[i];
      var itemPair = item.properties && String(item.properties._kinsora_bogo_pair || '');
      var itemUnit = item.properties && String(item.properties._kinsora_cart_unit || '');
      if (Number(item.variant_id) === Number(variantId)
          && (!pairId || itemPair === pairId)
          && (!cartUnit || itemUnit === cartUnit)) return item;
    }
    return null;
  }

  function replacementSourceLine(cart, oldKey, pairId, replacementUnit, oldVariantId) {
    if (!cart || !Array.isArray(cart.items)) return null;
    var exact = cart.items.filter(function (item) {
      return String(item.key || '') === String(oldKey || '');
    })[0];
    if (exact) return exact;
    // Function-applied discounts can change a Shopify line key. The private
    // pair marker identifies the original free selection; exclude the new
    // exact-unit marker so a changed key cannot masquerade as a completed swap.
    var pairMatch = cart.items.filter(function (item) {
      var properties = item && item.properties || {};
      return String(properties._kinsora_bogo_pair || '') === String(pairId || '')
        && String(properties._kinsora_cart_unit || '') !== String(replacementUnit || '');
    })[0];
    if (pairMatch) return pairMatch;
    return cart.items.filter(function(item) {
      var properties = item && item.properties || {};
      return Number(oldVariantId) > 0
        && Number(item.variant_id) === Number(oldVariantId)
        && String(properties._kinsora_cart_unit || '') !== String(replacementUnit || '');
    })[0] || null;
  }

  function cartHasFreePouch(cart) {
    if (!cart || !cart.items) return false;
    var pouchId = Number(window.Kinsora && window.Kinsora.freeGiftAutoVariantId || 0);
    return cart.items.some(function (item) {
      var role = String(item.properties && item.properties._kinsora_free_gift_role || '').toLowerCase();
      return Number(item.variant_id) === pouchId && role !== 'bracelet' &&
        item.properties && String(item.properties._kinsora_free_gift || '').toLowerCase() === 'true' &&
        Number(item.final_line_price) === 0;
    });
  }

  function addOrChangeOption(button) {
    var card = button.closest('[data-ks-option]');
    if (!card) return;
    if (card.closest('[data-ks-sheet="picker"]')) return;
    var selected = optionSelection(card);
    if (!selected.variantId) {
      sheetStatus(state.activeSheet, 'Choose an available option first.');
      return;
    }
    if (card.hasAttribute('data-ks-inline-card')) { addInlineChoice(button); return; }
    var pickerCard = !!card.closest('[data-ks-sheet="picker"]');
    if (pickerCard && (card.hidden || !pickerSelectionAllowed(card, selected))) {
      hydrateOptions();
      sheetStatus(state.activeSheet, 'Choose an available option first.');
      return;
    }
    var changingFreeItem = pickerCard && state.mode === 'change';
    var pairId = pickerCard && state.pairId ? state.pairId : ('bogo-' + Date.now());
    var productTitle = card.dataset.displayName || card.dataset.productTitle || 'Product';
    var oldKey = changingFreeItem ? state.oldFreeKey : '';
    var oldVariantId = changingFreeItem ? state.oldFreeVariantId : 0;
    if (changingFreeItem && !oldKey) {
      sheetStatus(state.activeSheet, 'Your current FREE item could not be verified. Close this picker, refresh your cart, and try again.');
      return;
    }
    var previousBogoFreeCount = state.completedPairCount;
    var baselineCart = null;
    var pricing = null;
    var cartUnit = 'bogo-option-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);

    runMutation(button, function () {
      delete button.dataset.ksSuccessMessage;
      pricing = collectBogoPricing(selected);
      var baseline = pickerCard && !changingFreeItem ? fetchJson('/cart.js') : Promise.resolve(null);
      return baseline.then(function (cartBeforeAdd) {
        baselineCart = cartBeforeAdd;
        if (baselineCart) previousBogoFreeCount = actualBogoFreeCount(baselineCart, pricing);
        return fetchJson('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: selected.variantId,
              quantity: 1,
              properties: {
                _kinsora_bogo_pair: pairId,
                _kinsora_cart_unit: cartUnit
              }
            }]
          })
        }).catch(function (addError) {
          if (addError.kinsoraCartRequestTimeout) throw addError;
          // A lost add response may still have committed. The unique cart-unit
          // marker lets us recover exactly once without duplicating the product.
          return fetchJson('/cart.js').then(function (cartAfterAddFailure) {
            if (addedLineFromCart(cartAfterAddFailure, selected.variantId, pairId, cartUnit)) {
              return cartAfterAddFailure;
            }
            throw addError;
          }, function (readError) {
            readError.kinsoraUnsafeCartState = true;
            readError.kinsoraOperationError = addError;
            throw readError;
          });
        });
      }).then(function () {
        if (!oldKey) return fetchJson('/cart.js');
        return fetchJson('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: oldKey, quantity: 0 })
        }).catch(function (removeError) {
          if (removeError.kinsoraCartRequestTimeout) throw removeError;
          // The response can be lost after Shopify commits either write. Read
          // the authoritative cart before deciding whether the replacement
          // succeeded or the new exact-marker line must be rolled back.
          return fetchJson('/cart.js').then(function (cartAfterFailure) {
            var oldRemains = !!replacementSourceLine(cartAfterFailure, oldKey, pairId, cartUnit, oldVariantId);
            var replacement = addedLineFromCart(cartAfterFailure, selected.variantId, pairId, cartUnit);
            if (!oldRemains && replacement) return cartAfterFailure;
            if (oldRemains && !replacement) throw removeError;
            if (!oldRemains && !replacement) {
              var missingLinesError = new Error('The BOGO replacement result could not be identified. Please reload your cart.');
              missingLinesError.kinsoraUnsafeCartState = true;
              missingLinesError.kinsoraOperationError = removeError;
              throw missingLinesError;
            }

            return fetchJson('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: replacement.key, quantity: 0 })
            }).catch(function (rollbackError) {
              if (rollbackError.kinsoraCartRequestTimeout) throw rollbackError;
              // A lost rollback response is also ambiguous; the final read is
              // the only source of truth.
              return null;
            }).then(function () {
              return fetchJson('/cart.js');
            }).then(function (cartAfterRollback) {
              var oldStillRemains = !!replacementSourceLine(cartAfterRollback, oldKey, pairId, cartUnit, oldVariantId);
              var replacementStillRemains = addedLineFromCart(cartAfterRollback, selected.variantId, pairId, cartUnit);
              if (!oldStillRemains && replacementStillRemains) return cartAfterRollback;
              if (oldStillRemains && !replacementStillRemains) {
                removeError.kinsoraBogoSwapRolledBack = true;
                throw removeError;
              }
              var unsafeError = new Error('The BOGO replacement could not be safely completed or rolled back. Please reload your cart.');
              unsafeError.kinsoraUnsafeCartState = true;
              unsafeError.kinsoraOperationError = removeError;
              throw unsafeError;
            });
          }, function (readError) {
            readError.kinsoraUnsafeCartState = true;
            readError.kinsoraOperationError = removeError;
            throw readError;
          });
        });
      }).then(function (cart) {
        var line = addedLineFromCart(cart, selected.variantId, pairId, cartUnit);
        if (!line) {
          var unconfirmedAddError = new Error('The selected item could not be confirmed in your cart. Please reload your cart.');
          unconfirmedAddError.kinsoraUnsafeCartState = true;
          throw unconfirmedAddError;
        }
        var confirmedBogoFreeCount = actualBogoFreeCount(cart, pricing);
        var verifiedTie = pickerCard && !changingFreeItem && verifiedNoCostBogoTie(baselineCart, cart, line, selected, pricing);
        var totalUnchanged = baselineCart && cart
          && baselineCart.currency === cart.currency
          && Number(baselineCart.total_price) === Number(cart.total_price);
        if (pickerCard && !changingFreeItem && (!totalUnchanged || Number(line.quantity) !== 1 || confirmedBogoFreeCount <= previousBogoFreeCount || (Number(line.final_line_price) !== 0 && !verifiedTie))) {
          window.Kinsora = window.Kinsora || {};
          window.Kinsora.bogoDiscountNotice = {
            type: 'error',
            text: 'We could not confirm this selection at no extra cost, so we removed it. You were not charged. Please choose again.'
          };
          return fetchJson('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 })
          }).then(function (updatedCart) {
            button.dataset.ksSuccessMessage = productTitle + ' could not be added at no extra cost, so it was removed. You were not charged.';
            return updatedCart;
          });
        }
        if (confirmedBogoFreeCount > previousBogoFreeCount && window.Kinsora) {
          window.Kinsora.bogoDiscountNotice = null;
        }
        var result = line && Number(line.final_line_price) === 0
          ? productTitle + ' is now FREE with BOGO.'
          : verifiedTie
            ? productTitle + ' was added at no extra cost. Buy 1, Get 1 savings applied.'
            : productTitle + ' was added. Shopify recalculated your cart.';
        if (cartHasFreePouch(cart)) result += ' Your bonus travel pouch is included.';
        button.dataset.ksSuccessMessage = result;
        return cart;
      });
    }, productTitle + ' added.').then(function (cart) {
      if (!cart) return;
      var message = button.dataset.ksSuccessMessage;
      if (message) announce(message);
    });
  }

  function removeLine(key, control, message) {
    return runMutation(control, function () {
      var line = control && control.closest ? control.closest('[data-ks-cart-line]') : null;
      var syntheticSplitUnit = (control && control.dataset.syntheticSplitUnit === 'true')
        || (line && line.dataset.syntheticSplitUnit === 'true');
      if (syntheticSplitUnit) {
        return fetchJson('/cart.js').then(function (cart) {
          var sourceLine = cart && cart.items && cart.items.filter(function (item) { return item.key === key; })[0];
          if (!sourceLine) throw new Error('Cart line no longer exists.');
          return fetchJson('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: Math.max((Number(sourceLine.quantity) || 0) - 1, 0) })
          });
        });
      }
      return fetchJson('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
    }, message || 'Item removed from your cart.');
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var retryBogo = target.closest('[data-ks-retry-bogo]');
    if (retryBogo) {
      event.preventDefault();
      retryPendingBogoSavings(retryBogo);
      return;
    }

    var close = target.closest('[data-ks-close-sheet]');
    if (close) {
      event.preventDefault();
      closeSheet(true);
      return;
    }

    var changeChoice = target.closest('[data-ks-change-choice], [data-ks-change-line]');
    if (changeChoice) {
      event.preventDefault();
      if (inlineCartUpdating() || changeChoice.disabled) return;
      var chosen = inlineSelections(inline.cart);
      chooseInlineReplacement(changeChoice.dataset.ksChangeLine || (inline.removeKey ? '' : chosen.length ? chosen[chosen.length - 1].key : ''), changeChoice);
      return;
    }

    var openPicker = target.closest('[data-ks-open-picker]');
    if (openPicker) {
      event.preventDefault();
      configurePicker(openPicker);
      return;
    }

    var pickerToggle = target.closest('[data-ks-picker-toggle]');
    if (pickerToggle) {
      event.preventDefault();
      if (pickerToggle.closest('[data-ks-inline-chooser]')) {
        if (inlineCartUpdating()) return;
        inline.shown = inline.shown >= inline.eligible.length ? 4 : Math.min(inline.eligible.length, inline.shown + 4);
        renderInline(); return;
      }
      var pickerRoot = drawer();
      var pickerPanel = pickerRoot && pickerRoot.querySelector('[data-ks-picker-panel="' + selectorEscape(pickerToggle.dataset.ksPickerToggle) + '"]');
      if (!pickerPanel || pickerToggle.hidden) return;
      var expandPicker = pickerPanel.dataset.ksPickerExpanded !== 'true';
      pickerPanel.dataset.ksPickerExpanded = expandPicker ? 'true' : 'false';
      updatePickerDisclosure(pickerPanel, false);
      return;
    }

    var openSavings = target.closest('[data-ks-open-savings]');
    if (openSavings) {
      event.preventDefault();
      openSheet('savings', openSavings);
      return;
    }

    var openGiftPicker = target.closest('[data-ks-open-gift-picker]');
    if (openGiftPicker) {
      event.preventDefault();
      var openGiftRole = String(openGiftPicker.dataset.ksOpenGiftPicker || '');
      if (openGiftRole === 'pouch' || openGiftRole === 'bracelet') {
        openSheet(openGiftRole, openGiftPicker);
      }
      return;
    }

    var swapGift = target.closest('[data-ks-swap-gift]');
    if (swapGift) {
      event.preventDefault();
      var swapGiftRole = String(swapGift.dataset.ksSwapGift || '');
      var swapGiftSheet = swapGift.closest('[data-ks-sheet]');
      var swapUnavailableCopy = swapGiftSheet ? swapGiftSheet.dataset.swapUnavailableCopy : '';
      var swapSuccessCopy = swapGiftSheet ? swapGiftSheet.dataset.swapSuccessCopy : '';
      if ((swapGiftRole !== 'pouch' && swapGiftRole !== 'bracelet') || !window.Kinsora || typeof window.Kinsora.swapFreeGift !== 'function') {
        sheetStatus(state.activeSheet, swapUnavailableCopy);
        return;
      }
      runMutation(swapGift, function () {
        return window.Kinsora.swapFreeGift(swapGiftRole, Number(swapGift.dataset.variantId || 0));
      }, swapSuccessCopy, { operationOwnsSynchronization: true });
      return;
    }

    var savingsCheckout = target.closest('[data-ks-savings-checkout]');
    if (savingsCheckout) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeSheet(false);
      var checkout = drawer() && drawer().querySelector('.ks-cart-footer__checkout');
      if (checkout) checkout.click();
      return;
    }

    var variantChoice = target.closest('[data-ks-variant-choice]');
    if (variantChoice) {
      event.preventDefault();
      var variantCard = variantChoice.closest('[data-ks-option]');
      var variantControl = variantCard && variantCard.querySelector('[data-ks-option-variant]');
      if (!variantCard || !variantControl || variantChoice.disabled || variantChoice.hidden) return;
      if (variantCard.hasAttribute('data-ks-inline-card') && inlineCartUpdating()) return;
      variantControl.value = variantChoice.dataset.variantId || '';
      variantCard.dataset.optionPrice = variantChoice.dataset.price || variantCard.dataset.optionPrice;
      variantCard.dataset.optionFullPrice = variantChoice.dataset.fullPrice || variantCard.dataset.optionFullPrice;
      variantCard.dataset.optionOriginalPrice = variantChoice.dataset.originalPrice || variantCard.dataset.optionOriginalPrice;
      variantCard.querySelectorAll('[data-ks-variant-choice]').forEach(function (choice) {
        choice.setAttribute('aria-pressed', choice === variantChoice ? 'true' : 'false');
      });
      if (variantCard.hasAttribute('data-ks-inline-card')) {
        updateInlineVariant(variantCard, variantChoice);
        renderInline();
      } else hydrateOptions();
      return;
    }

    var add = target.closest('[data-ks-add-product]');
    if (add) {
      event.preventDefault();
      addOrChangeOption(add);
      return;
    }

    var remove = target.closest('[data-ks-remove-line]');
    if (remove) {
      event.preventDefault();
      removeLine(remove.dataset.lineKey, remove, remove.dataset.lineTitle + ' removed.');
      return;
    }
  }, true);

  document.addEventListener('change', function (event) {
    var control = event.target && event.target.closest && event.target.closest('[data-ks-option-variant]');
    if (!control) return;
    var card = control.closest('[data-ks-option]');
    if (card && control.tagName === 'SELECT') {
      var option = control.options[control.selectedIndex];
      if (option) {
        card.dataset.optionPrice = option.dataset.price || card.dataset.optionPrice;
        card.dataset.optionFullPrice = option.dataset.fullPrice || card.dataset.optionFullPrice;
        card.dataset.optionOriginalPrice = option.dataset.originalPrice || card.dataset.optionOriginalPrice;
      }
    }
    renderOptionOutcome(card);
  });

  document.addEventListener('keydown', function (event) {
    reconcileActiveSheet(false);
    if (!state.activeSheet) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeSheet(true);
      return;
    }
    if (event.key !== 'Tab') return;
    var panel = state.activeSheet.querySelector('.ks-sheet__panel');
    var focusable = focusableWithin(panel);
    if (!focusable.length) {
      event.preventDefault();
      panel.focus();
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, true);

  function beforeDrawerReplace() {
    captureInlineView();
    resetActiveSheet({ returnFocus: false, focusFallback: false });
    document.documentElement.classList.remove('ks-cart-sheet-open');
  }

  function afterDrawerReplace(options) {
    options = options || {};
    reconcileActiveSheet(false);
    var root = drawer(), section = root && root.querySelector('[data-ks-inline-chooser]');
    if (inline.view && options.restoreFocus) inline.view.restoreFocus = true;
    if (inlineCartUpdating()) {
      if (section && inline.frozenChooser && !inline.frozenChooser.hidden && section !== inline.frozenChooser) {
        inline.stagedChooser = section;
        section.parentNode.replaceChild(inline.frozenChooser, section);
        freezeInlineAction(inline.frozenChooser);
      } else if (!section) {
        // Empty carts must not retain a stale chooser or an empty spacer.
        inline.frozenChooser = null; inline.stagedChooser = null;
      }
      var body = root && root.querySelector('#cartBody');
      if (body && inline.view) body.scrollTop = inline.view.scrollTop;
    }
    hydrateOptions();
    restoreInlineView();
  }

  function syncDrawerVisibility(isOpen) {
    if (isOpen) {
      window.requestAnimationFrame(function () { startInline(); });
      reconcileActiveSheet(false);
      return;
    }
    resetActiveSheet({ returnFocus: false, focusFallback: false });
    document.documentElement.classList.remove('ks-cart-sheet-open');
  }

  function init() {
    if (!drawer()) return;
    refreshCartCountdown();
    reconcileActiveSheet(true);
    hydrateOptions();
    var section = document.querySelector('.section-cart-drawer');
    if (section && !section.__ksCartObserver) {
      section.__ksCartObserver = new MutationObserver(function (records) {
        // Explicit drawer replacement hooks handle normal cart updates. Keep a
        // fallback for replaced drawer/catalog nodes, not our own text/spinners.
        records.forEach(function (record) {
          var target = record.target;
          if (!target.matches) return;
          if (target.matches('[data-ks-inline-cart-state], [data-ks-inline-catalog]')) inline.node = null;
          if (target.matches('[data-ks-inline-products]')) { inline.productsLoaded = false; inline.node = null; }
        });
        var replaced = records.some(function (record) {
          if (record.target === section) return true;
          if (record.target.matches && record.target.matches('[data-ks-inline-cart-state], [data-ks-inline-catalog], [data-ks-inline-copy-data], [data-ks-inline-products], [data-ks-inline-card-template]')) return true;
          return Array.prototype.some.call(record.addedNodes || [], function (node) {
            if (node.nodeType !== 1) return false;
            var selector = '#cartDrawer, [data-ks-inline-chooser], [data-ks-inline-cart-state], [data-ks-inline-catalog], [data-ks-inline-products], [data-ks-inline-card-template]';
            return node.matches && node.matches(selector) || node.querySelector && !!node.querySelector(selector);
          });
        });
        if (!replaced || section.__ksCartObserverFrame) return;
        section.__ksCartObserverFrame = window.requestAnimationFrame(function () {
          section.__ksCartObserverFrame = 0;
          reconcileActiveSheet(true);
          hydrateOptions();
        });
      });
      section.__ksCartObserver.observe(section, { childList: true, subtree: true });
    }
  }

  window.KinsoraCartRedesign = {
    captureMutationView: captureInlineView,
    isMutationBusy: function () { return state.busy; },
    init: init,
    hydrateOptions: hydrateOptions,
    beforeDrawerReplace: beforeDrawerReplace,
    afterDrawerReplace: afterDrawerReplace,
    syncDrawerVisibility: syncDrawerVisibility
  };
  syncVisualViewportInset();
  window.addEventListener('resize', syncVisualViewportInset, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', syncVisualViewportInset, { passive: true });
    window.visualViewport.addEventListener('scroll', syncVisualViewportInset, { passive: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('.ks-cart-footer__checkout');
    if (!button || state.busy || button.getAttribute('aria-busy') === 'true' || button.getAttribute('aria-disabled') === 'true') return;
    var key = drawer().dataset.cartLineFingerprint;
    if (inline.checkoutEvent === key) return;
    inline.checkoutEvent = key;
    inlineEvent('checkout_click', {selected_choices: inlineSelections(inlineCurrentCart()).length});
  }, true);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('kinsora:cart-drawer-updated', refreshCartCountdown);
  document.addEventListener('visibilitychange', refreshCartCountdown);
  window.addEventListener('pageshow', refreshCartCountdown);
  window.addEventListener('storage', function (event) {
    if (event.key !== cartCountdownStorageKey && event.key !== null) return;
    var sharedDeadline = readCartCountdownDeadline();
    if (sharedDeadline !== null) cartCountdownDeadline = sharedDeadline;
    refreshCartCountdown();
  });
})();
